// EnhancedPOIForm.js - Complete with Hidden Field Support and IMPROVED ERROR HANDLING
import React, { useState, useEffect, useMemo } from 'react';
import { PlusCircle, Loader, Wand2, Eye, X, Info, AlertCircle, Upload, Camera, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';

import ImageUploader from '../ItineraryCreator/ui/ImageUploader';
import SimpleCoordinatesSection from '../maps/SimpleCoordinatesSection';
import VoiceControl from '../ItineraryCreator/SimpleVoiceControl';
import { usePromptTemplates } from '../../components/promptTemplates/index_prompt_templates.js';
import ModelSelector from '../selectors/ModelSelector';
import { useSimpleAudioGeneration } from '../../components/hooks/useSimpleAudioGeneration';
import { convertToSSML } from '../ItineraryCreator/utils/ssmlUtils';
import { getAccessibleAudioUrl } from '../ItineraryCreator/utils/audioUtils';
import {
  AUDIO_COMPRESSION_PRESETS,
  compressAndUploadAudioVariant,
} from '../ItineraryCreator/utils/audioCompression';
import { calculateTTSCost, getVoicePricing } from '../ItineraryCreator/utils/ttsPricing';
import { findVoiceForLanguage } from '../ItineraryCreator/utils/voiceConstants';
import { buildAudioGenerationStamp } from '../audio/audioGenerationInfo';
import StopArchetypeClassifier from '../ItineraryCreator/utils/StopArchetypeClassifier';
import { LANGUAGES } from '../selectors/LanguageSelector';
// Access control imports
import { useAccessControl } from '../../contexts/AccessControlContext';
import FeatureGuard from './../FeatureGuard';
import { useAuth } from '../../contexts/AuthContext';
import { useGuides } from '../../contexts/GuidesContext';
import { buildRelatedGuideFromPublishedGroup, groupPublishedGuides, matchesPublishedGuideSearch } from '../utils/publishedGuides';

const LOCKED_TRANSLATED_POI_FIELDS = new Set([
  'category',
  'tier',
  'photoUrl',
  'sphericalPhotoUrl',
  'signLanguageVideoUrl',
  'relatedGuide',
  'coordinates',
  'orientation',
  'useSSML',
  'estimatedDuration',
  'openingHours',
  'keywords',
  'flexibleContent'
]);

const ERINOME_CITYRAMA_STYLE_PREFIX = '[high-pitched], [playful], [excited]';
const MIN_AUDIO_CHUNK_SIZE = 100;
const EMPTY_COMPRESSED_AUDIO_VARIANTS = [];

const applyErinomeCityramaPrefix = (text) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return ERINOME_CITYRAMA_STYLE_PREFIX;
  if (normalizedText.startsWith(ERINOME_CITYRAMA_STYLE_PREFIX)) {
    return normalizedText;
  }
  return `${ERINOME_CITYRAMA_STYLE_PREFIX} ${normalizedText}`;
};

const resolveAudioChunkSize = (value, fallback = 800) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(MIN_AUDIO_CHUNK_SIZE, Math.floor(parsed));
};

const estimateAudioDurationSeconds = (text, speechRate = 1) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return 0;

  const charsPerMinute = 750;
  const numericSpeechRate = Number(speechRate);
  const safeSpeechRate = Number.isFinite(numericSpeechRate) && numericSpeechRate > 0
    ? numericSpeechRate
    : 1;

  return (normalizedText.length / charsPerMinute) * 60 / safeSpeechRate;
};

const formatEstimatedAudioDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0s';

  const roundedSeconds = Math.max(1, Math.round(seconds));
  const minutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;

  if (minutes === 0) return `${remainingSeconds}s`;
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
};

const areStringMapsEqual = (left, right) => {
  const leftKeys = Object.keys(left || {});
  const rightKeys = Object.keys(right || {});

  if (leftKeys.length !== rightKeys.length) {
    return false;
  }

  return leftKeys.every((key) => left[key] === right[key]);
};

const splitAudioTextIntoChunks = (text, maxChunkSize = 800) => {
  const normalized = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!normalized || normalized.length <= maxChunkSize) {
    return normalized ? [normalized] : [];
  }

  const paragraphs = normalized.split('\n\n').map((part) => part.trim()).filter(Boolean);
  if (paragraphs.length <= 1) {
    const chunks = [];
    let remaining = normalized;
    while (remaining.length > maxChunkSize) {
      let splitPoint = remaining.lastIndexOf(' ', maxChunkSize);
      if (splitPoint < Math.floor(maxChunkSize * 0.6)) {
        splitPoint = maxChunkSize;
      }
      chunks.push(remaining.slice(0, splitPoint).trim());
      remaining = remaining.slice(splitPoint).trim();
    }
    if (remaining) chunks.push(remaining);
    return chunks;
  }

  const chunks = [];
  let currentChunk = '';
  paragraphs.forEach((paragraph) => {
    const candidate = currentChunk ? `${currentChunk}\n\n${paragraph}` : paragraph;
    if (currentChunk && candidate.length > maxChunkSize) {
      chunks.push(currentChunk);
      currentChunk = paragraph;
    } else {
      currentChunk = candidate;
    }
  });
  if (currentChunk) chunks.push(currentChunk);
  return chunks;
};

const FieldWithGeneration = ({
  label,
  value,
  onChange,
  onGenerate,
  onPreviewPrompt,
  isGenerating,
  error,
  type = 'text',
  placeholder = '',
  multiline = false,
  showPromptPreview = false,
  required = false,
  wordCount = null,
  maxWords = null,
  audioEstimate = '',
  fieldName = '',
  poi = null,
  disabled = false,
}) => (
  <div className={`space-y-1 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
    <div className="flex justify-between items-center">
      <label className="block text-sm font-medium mb-1">
        {label} {required && <span className="text-red-500">*</span>}
        {disabled && <span className="text-gray-500 text-xs ml-2">(View Only)</span>}
      </label>
    </div>
    <div className="relative flex gap-2 items-center">
      {multiline ? (
        <textarea
          className={`flex-1 p-2 border rounded ${error ? 'border-red-500' : ''} ${required && !value ? 'border-yellow-500' : ''} ${wordCount > maxWords ? 'border-red-500' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          value={value || ''}
          onChange={(e) => !disabled && onChange(e.target.value)}
          placeholder={disabled ? 'View Only Mode' : placeholder}
          rows={4}
          disabled={disabled}
          readOnly={disabled}
        />
      ) : (
        <input
          type={type}
          className={`flex-1 p-2 border rounded ${error ? 'border-red-500' : ''} ${required && !value ? 'border-yellow-500' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
          value={value || ''}
          onChange={(e) => !disabled && onChange(e.target.value)}
          placeholder={disabled ? 'View Only Mode' : placeholder}
          disabled={disabled || (fieldName === 'description' && (!poi || !poi.inDepthDescription))}
          readOnly={disabled}
        />
      )}
      {showPromptPreview && onPreviewPrompt && (
        <button
          onClick={onPreviewPrompt}
          className={`px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1 ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          title="Preview API Prompt"
          disabled={disabled}
        >
          <Eye className="w-4 h-4" />
        </button>
      )}
      {onGenerate && (
        <button
          onClick={onGenerate}
          disabled={disabled || isGenerating || (fieldName === 'description' && (!poi || !poi.inDepthDescription))}
          className={`px-3 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-400 flex items-center gap-1 ${disabled ? 'cursor-not-allowed' : ''}`}
          title={disabled ? "View Only Mode" : (fieldName === 'description' && (!poi || !poi.inDepthDescription) ? "Please fill In-Depth Description first" : "")}
        >
          {isGenerating ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <Wand2 className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
    {wordCount !== null && maxWords !== null && (
      <div className={`mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs ${wordCount > maxWords ? 'text-red-500 font-medium' : 'text-gray-500'}`}>
        <span>Word count: {wordCount}</span>
        <span>{wordCount > maxWords ? `${wordCount - maxWords} words over limit!` : `${maxWords - wordCount} words remaining`}</span>
        {audioEstimate && <span>Est. audio: {audioEstimate}</span>}
      </div>
    )}
    {error && <p className="text-red-500 text-sm">{error}</p>}
    {required && !value && !error && (
      <p className="text-yellow-600 text-sm flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> This field is required
      </p>
    )}
    {wordCount > maxWords && !error && (
      <p className="text-red-500 text-sm flex items-center gap-1">
        <AlertCircle className="w-3 h-3" /> Description exceeds the maximum word count for audio generation
      </p>
    )}
  </div>
);

// Keywords Display Component
const KeywordsDisplay = ({ keywords, onClear, onRemoveKeyword, disabled = false }) => {
  const keywordArray = Array.isArray(keywords) ? keywords : [];

  if (keywordArray.length === 0) {
    return null;
  }

  return (
    <div className={`bg-green-50 border border-green-200 rounded-lg p-4 mt-3 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      <div className="flex justify-between items-center mb-2">
        <h4 className="font-medium text-green-800">
          Current Keywords: {disabled && <span className="text-gray-500 text-xs ml-2">(View Only)</span>}
        </h4>
        <button
          onClick={onClear}
          className={`text-green-600 hover:text-green-800 text-sm ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          disabled={disabled}
        >
          Clear All
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {keywordArray.map((keyword, index) => (
          <span
            key={index}
            className={`px-2 py-1 bg-white border border-green-300 rounded text-sm text-green-700 flex items-center gap-1 group ${disabled ? 'cursor-not-allowed' : 'hover:bg-red-50 hover:border-red-300'} transition-colors`}
          >
            {keyword}
            <button
              onClick={() => !disabled && onRemoveKeyword(index)}
              className={`ml-1 text-green-500 hover:text-red-500 ${disabled ? 'opacity-30 cursor-not-allowed' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}
              title={disabled ? "View Only Mode" : `Remove "${keyword}"`}
              disabled={disabled}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <p className="text-xs text-green-600 mt-2">
        {disabled
          ? "View Only Mode - Keywords cannot be modified"
          : "Click the × on any keyword to remove it. Only remaining keywords will be saved."
        }
      </p>
    </div>
  );
};

// Improved Error Summary Component
const ErrorSummary = ({ missingFields, onFieldClick }) => {
  if (missingFields.length === 0) return null;

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="font-medium text-red-800 mb-2">
            {missingFields.length} Required Field{missingFields.length > 1 ? 's' : ''} Missing
          </h4>
          <p className="text-sm text-red-700 mb-3">
            Please complete these fields before saving your Point of Interest:
          </p>
          <ul className="space-y-2">
            {missingFields.map((field, index) => (
              <li key={index} className="flex items-start gap-2">
                <span className="text-red-500 text-sm mt-1">•</span>
                <div>
                  <button
                    onClick={() => onFieldClick(field)}
                    className="text-sm text-red-700 hover:text-red-900 underline text-left"
                  >
                    {field}
                  </button>
                  {field === 'Keywords (required for stop points)' && (
                    <p className="text-xs text-red-600 mt-1">
                      → Scroll up to "Keywords & Related Topics" section and add at least one keyword
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

// Success Message Component
const SuccessMessage = () => (
  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-2">
    <div className="flex items-center gap-2 text-green-700">
      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
      <span className="text-sm font-medium">All required fields completed - ready to save!</span>
    </div>
  </div>
);

// Prompt Preview Component
const PromptPreview = ({ prompt, field, previousPois, onClose }) => {
  if (!prompt) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-500" />
            API Prompt for: {field}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {previousPois && previousPois.length > 0 && (
          <div className="p-4 bg-blue-50 border-b">
            <p className="font-medium text-blue-800">Including context from {previousPois.length} previous POIs:</p>
            <div className="flex flex-wrap gap-2 mt-2">
              {previousPois.map((poi, index) => (
                <span key={index} className="px-2 py-1 bg-white border border-blue-200 rounded text-sm">
                  {poi}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-auto flex-1 p-4">
          <pre className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded border">
            {prompt}
          </pre>
        </div>

        <div className="p-4 border-t flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// SSML Preview Component
const SSMLPreview = ({ ssml, onClose }) => {
  if (!ssml) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-8">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-500" />
            SSML Preview
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-auto flex-1 p-4">
          <p className="mb-4 text-sm text-gray-600">
            Below is the SSML markup that will be sent to the text-to-speech service. Notice how initials like "J.K." will be properly pronounced.
          </p>
          <pre className="whitespace-pre-wrap text-sm bg-gray-50 p-4 rounded border overflow-auto">
            {ssml}
          </pre>
        </div>

        <div className="p-4 border-t flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export const EnhancedPOIForm = (props) => {
  const {
    poi,
    onPoiChange,
    onGenerateField,
    isGenerating,
    errors = {},
    onSubmit,
    categories,
    onImageUpload,
    isUploading,
    itineraryMetadata = {},
    itineraryPoints = [],
    voiceStyle,
    selectedModel,
    onModelChange,
    isTranslatingAll,
    translatingFields,
  } = props;

  const { currentUser } = useAuth();
  const { fetchGuides, getCachedGuides } = useGuides();
  // Access control hooks
  const { hasFieldAccess, getFieldAccess, canPerformAction } = useAccessControl();
  const isTranslatedGuide = itineraryMetadata?.translated_itinerary === 'yes';
  const isTranslatedSharedPoiFieldLocked = (fieldKey) =>
    isTranslatedGuide && LOCKED_TRANSLATED_POI_FIELDS.has(fieldKey);

  // State for expanded sections
  const [isContextExpanded, setIsContextExpanded] = useState(false);
  const [isNextStopExpanded, setIsNextStopExpanded] = useState(false);
  const [isModelSelectorExpanded, setIsModelSelectorExpanded] = useState(false);
  const [isLocationExpanded, setIsLocationExpanded] = useState(false);
  const [isMediaExpanded, setIsMediaExpanded] = useState(false);
  const [isAudioNarrationExpanded, setIsAudioNarrationExpanded] = useState(false);
  const [isAudioSetupExpanded, setIsAudioSetupExpanded] = useState(false);
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);
  const [isSupplementaryExpanded, setIsSupplementaryExpanded] = useState(false);
  const [isRelatedGuideExpanded, setIsRelatedGuideExpanded] = useState(false);
  const [showSphericalUploader, setShowSphericalUploader] = useState(!!poi.sphericalPhotoUrl);
  const [isVoiceControlExpanded, setIsVoiceControlExpanded] = useState(false);
  const [isGeminiStyleExpanded, setIsGeminiStyleExpanded] = useState(false);
  const [isChunkPreviewExpanded, setIsChunkPreviewExpanded] = useState(false);
  const [publishedGuides, setPublishedGuides] = useState(() => getCachedGuides('all') || []);
  const [isLoadingPublishedGuides, setIsLoadingPublishedGuides] = useState(false);
  const [relatedGuideSearch, setRelatedGuideSearch] = useState('');

  useEffect(() => {
    const cachedGuides = getCachedGuides('all');
    if (cachedGuides?.length) {
      setPublishedGuides(cachedGuides);
    }
  }, [getCachedGuides]);

  useEffect(() => {
    const loadPublishedGuides = async () => {
      if (!currentUser) return;

      const cachedGuides = getCachedGuides('all');
      if (cachedGuides?.length) {
        setPublishedGuides(cachedGuides);
        return;
      }

      try {
        setIsLoadingPublishedGuides(true);
        const result = await fetchGuides('all');
        setPublishedGuides(result.guides || []);
      } catch (error) {
        console.error('Failed to load published guides for related guide picker:', error);
      } finally {
        setIsLoadingPublishedGuides(false);
      }
    };

    loadPublishedGuides();
  }, [currentUser, fetchGuides, getCachedGuides]);

  const publishedGuideGroups = useMemo(
    () => groupPublishedGuides(publishedGuides),
    [publishedGuides]
  );

  const filteredPublishedGuideGroups = useMemo(
    () => publishedGuideGroups.filter((group) => matchesPublishedGuideSearch(group, relatedGuideSearch)).slice(0, 12),
    [publishedGuideGroups, relatedGuideSearch]
  );

  useEffect(() => {
    if (!poi.relatedGuide) return;
    if (relatedGuideSearch) return;

    const defaultSearchLabel = poi.relatedGuide.name || poi.relatedGuide.guideId || '';
    if (defaultSearchLabel) {
      setRelatedGuideSearch(defaultSearchLabel);
    }
  }, [poi.relatedGuide, relatedGuideSearch]);
  const [isVideosExpanded, setIsVideosExpanded] = useState(false);

  // Sync spherical uploader visibility when POI prop changes
  useEffect(() => {
    setShowSphericalUploader(!!poi.sphericalPhotoUrl);
  }, [poi.sphericalPhotoUrl]);

  // Use the centralized prompt management hook
  const { generatePromptForField, getPromptForVoice } = usePromptTemplates(voiceStyle);

  // Helper function for generating field content with proper prompts
  const generateFieldWithPrompt = (field) => {
    const filledPrompt = generatePromptForField(field, poi, itineraryMetadata, itineraryPoints);
    onGenerateField(field, filledPrompt);
  };

  const [speechRate, setSpeechRate] = useState('1');
  const [chunkingEnabled, setChunkingEnabled] = useState(true);
  const [chunkSize, setChunkSize] = useState(800);
  // Use the enhanced audio generation hook
  const { generateAudio, isGenerating: isGeneratingAudio, error: audioError } = useSimpleAudioGeneration();

  // Determine if this is a stop point
  const isStopPoint = poi.category && poi.category.toLowerCase().includes('stop');
  const isBusTourGuide = itineraryMetadata?.guide_type === 'bus_tour';
  const isBusStopPoint = poi.pointType === 'popup_stop' || poi.pointType === 'bus_stop' || poi.category === 'popup_stop' || poi.category === 'bus_stop';
  const isNarratedStopPoint = !isBusStopPoint;

  // State for TTS voice selection
  const [ttsVoice, setTtsVoice] = useState(
    poi.ttsVoice || findVoiceForLanguage(itineraryMetadata?.language || 'en-US')
  );

  const getDefaultChunkSizeForVoice = (voiceId) => {
    if (!voiceId) return 800;
    // Gemini 3.8 stays stable on long audio, so only very long texts get split
    if (voiceId.includes('SimpleTTS-38')) return 5000;
    if (voiceId.includes('Erinome')) return 600;
    if (voiceId.includes('SimpleTTS-Legacy') || voiceId.includes('SimpleTTS-Pro')) return 1000;
    if (voiceId.includes('SimpleTTS')) return 800;
    return 800;
  };

  // State for Gemini voice style controls - Defaults: Sarcastic Tour Guide preset
  const [geminiTone, setGeminiTone] = useState(55); // 0-100: calm to excited (55 = sarcastic preset)
  const [geminiPace, setGeminiPace] = useState(70); // 0-100: slow to fast (70 = sarcastic preset)
  const [geminiFormality, setGeminiFormality] = useState(25); // 0-100: casual to formal (25 = casual)
  const [geminiTemperature, setGeminiTemperature] = useState(1.55); // 0.0-2.0: deterministic to creative (1.55 = sweet spot)
  const [geminiCustomPrompt, setGeminiCustomPrompt] = useState('You are Sofia, a witty and slightly irreverent tour guide who has seen it all. Deliver the content with dry humor and occasional gentle sarcasm, as if sharing secrets with a friend at a cafe. Use playful skepticism when mentioning historical legends. Maintain a conversational, unhurried pace with dramatic pauses before punchlines. Your tone should be warm but knowingly amused.');
  const [personalityPreset, setPersonalityPreset] = useState('sarcastic'); // Personality preset selection (default: sarcastic)

  // State for cost estimation
  const [estimatedCost, setEstimatedCost] = useState(null);

  // State for managing prompt preview
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [previewField, setPreviewField] = useState('');
  const [previousPois, setPreviousPois] = useState([]);
  const [internalErrors, setInternalErrors] = useState({});

  // State for SSML preview
  const [ssmlPreview, setSsmlPreview] = useState("");
  const [showSsmlPreview, setShowSsmlPreview] = useState(false);

  // State for tracking word counts
  const [inDepthDescriptionWordCount, setInDepthDescriptionWordCount] = useState(0);
  const MAX_WORDS_FOR_AUDIO = 650;
  const orderedItineraryPoints = Array.isArray(itineraryPoints) ? itineraryPoints : [];
  const currentPoiIndex = orderedItineraryPoints.findIndex((point) => point.id === poi.id);

  // State for generated keywords
  const [generatedKeywords, setGeneratedKeywords] = useState([]);

  // State for manual keyword input
  const [manualKeyword, setManualKeyword] = useState('');

  // State for accessible audio URL
  const [accessibleAudioUrl, setAccessibleAudioUrl] = useState('');
  const [originalAccessibleAudioUrl, setOriginalAccessibleAudioUrl] = useState('');
  const [compressedAccessibleAudioUrls, setCompressedAccessibleAudioUrls] = useState({});

  // State for external audio URL input
  const [externalAudioUrl, setExternalAudioUrl] = useState('');
  const [currentAudioUrlCopied, setCurrentAudioUrlCopied] = useState(false);
  const [compressionBusyPresetId, setCompressionBusyPresetId] = useState('');
  const [selectedCompressionPresetId, setSelectedCompressionPresetId] = useState('mp3-48k');
  const [compressionStatus, setCompressionStatus] = useState('');
  const [compressionError, setCompressionError] = useState('');
  const compressedAudioVariants = useMemo(
    () => (Array.isArray(poi.compressedAudioVariants) ? poi.compressedAudioVariants : EMPTY_COMPRESSED_AUDIO_VARIANTS),
    [poi.compressedAudioVariants]
  );
  const uncompressedAudioUrl = poi.uncompressedAudioUrl || poi.originalAudioUrl || '';
  const currentAudioUrl = poi.audioUrl || uncompressedAudioUrl || poi.originalAudioUrl || '';
  const defaultCompressionPreset = AUDIO_COMPRESSION_PRESETS.find(p => p.id === 'mp3-48k') || AUDIO_COMPRESSION_PRESETS[0];
  const selectedCompressionPreset = AUDIO_COMPRESSION_PRESETS.find(p => p.id === selectedCompressionPresetId) || defaultCompressionPreset;

  // Function to count words in text
  const countWords = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).length;
  };

  // Update word count when in-depth description changes
  useEffect(() => {
    setInDepthDescriptionWordCount(countWords(poi.inDepthDescription));
  }, [poi.inDepthDescription]);

  // Update accessible audio URL when POI audio URL changes
  useEffect(() => {
    const updateAccessibleAudioUrl = async () => {
      const candidates = [poi.audioUrl, uncompressedAudioUrl, poi.originalAudioUrl].filter(Boolean);

      if (candidates.length === 0) {
        setAccessibleAudioUrl('');
        return;
      }

      for (const candidate of candidates) {
        try {
          const accessibleUrl = await getAccessibleAudioUrl(candidate, currentUser);
          setAccessibleAudioUrl(accessibleUrl);
          return;
        } catch (error) {
          console.error('Failed to get accessible audio URL for candidate:', candidate, error);
        }
      }

      setAccessibleAudioUrl(candidates[0]);
    };

    updateAccessibleAudioUrl();
  }, [poi.audioUrl, uncompressedAudioUrl, poi.originalAudioUrl, currentUser]);

  useEffect(() => {
    const updateOriginalAccessibleAudioUrl = async () => {
      if (uncompressedAudioUrl) {
        try {
          const accessibleUrl = await getAccessibleAudioUrl(uncompressedAudioUrl, currentUser);
          setOriginalAccessibleAudioUrl(accessibleUrl);
        } catch (error) {
          console.error('Failed to get accessible original audio URL:', error);
          setOriginalAccessibleAudioUrl(uncompressedAudioUrl);
        }
      } else {
        setOriginalAccessibleAudioUrl('');
      }
    };

    updateOriginalAccessibleAudioUrl();
  }, [uncompressedAudioUrl, currentUser]);

  useEffect(() => {
    return () => {
      if (accessibleAudioUrl && accessibleAudioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(accessibleAudioUrl);
      }
    };
  }, [accessibleAudioUrl]);

  useEffect(() => {
    let cancelled = false;
    const blobUrls = [];

    const resolveCompressedUrls = async () => {
      if (!compressedAudioVariants.length) {
        setCompressedAccessibleAudioUrls((previous) => (
          Object.keys(previous).length ? {} : previous
        ));
        return;
      }

      const resolved = {};

      for (const variant of compressedAudioVariants) {
        if (!variant?.audioUrl) continue;

        try {
          const accessibleUrl = await getAccessibleAudioUrl(variant.audioUrl, currentUser);
          resolved[variant.presetId] = accessibleUrl;
          if (typeof accessibleUrl === 'string' && accessibleUrl.startsWith('blob:')) {
            blobUrls.push(accessibleUrl);
          }
        } catch (error) {
          console.error('Failed to get accessible compressed audio URL:', error);
          resolved[variant.presetId] = variant.audioUrl;
        }
      }

      if (!cancelled) {
        setCompressedAccessibleAudioUrls((previous) => (
          areStringMapsEqual(previous, resolved) ? previous : resolved
        ));
      } else {
        blobUrls.forEach((url) => URL.revokeObjectURL(url));
      }
    };

    resolveCompressedUrls();

    return () => {
      cancelled = true;
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [compressedAudioVariants, currentUser]);

  // Check for mandatory fields on initialization and changes
  useEffect(() => {
    const validateMandatoryFields = () => {
      const newErrors = {};
      if (!poi.name) newErrors.name = "Name is required";
      if (!poi.category) newErrors.category = "Category is required";
      if (!poi.coordinates.latitude || !poi.coordinates.longitude) {
        newErrors.coordinates = "Coordinates are required";
      }
      if (isNarratedStopPoint && !poi.inDepthDescription) {
        newErrors.inDepthDescription = "In-depth description is required";
      }

      // Add keywords validation for stop points
      if (isStopPoint && !isBusStopPoint) {
        if (!poi.keywords ||
          (Array.isArray(poi.keywords) && poi.keywords.length === 0) ||
          (typeof poi.keywords === 'string' && poi.keywords.trim() === '')
        ) {
          newErrors.keywords = "Keywords are required for stop points";
        }
      }

      setInternalErrors(newErrors);
    };

    validateMandatoryFields();
  }, [poi, isNarratedStopPoint, isStopPoint, isBusStopPoint]);

  // Calculate estimated TTS cost whenever voice or text changes
  useEffect(() => {
    if (poi.inDepthDescription && ttsVoice) {
      const characterCount = poi.inDepthDescription.length;
      const cost = calculateTTSCost(ttsVoice, characterCount);
      setEstimatedCost(cost);
    } else {
      setEstimatedCost(null);
    }
  }, [poi.inDepthDescription, ttsVoice]);

  useEffect(() => {
    setChunkSize(getDefaultChunkSizeForVoice(ttsVoice));
  }, [ttsVoice]);

  // Helper function to check if we have valid keywords
  const hasValidKeywords = () => {
    if (!poi.keywords) return false;
    if (Array.isArray(poi.keywords)) {
      return poi.keywords.length > 0 && poi.keywords.some(k => k.trim() !== '');
    }
    if (typeof poi.keywords === 'string') {
      return poi.keywords.trim().length > 0;
    }
    return false;
  };

  // Function to get missing required fields
  const getMissingFields = () => {
    const missingFields = [];
    if (!poi.name) missingFields.push('Name');
    if (!poi.category) missingFields.push('Category');
    if (!poi.coordinates.latitude || !poi.coordinates.longitude) missingFields.push('Coordinates');
    if (isNarratedStopPoint && !poi.inDepthDescription) missingFields.push('In-depth Description');

    const missingKeywords = isStopPoint && !isBusStopPoint && !hasValidKeywords();
    if (missingKeywords) missingFields.push('Keywords (required for stop points)');

    return missingFields;
  };

  // Function to scroll to a specific field
  const scrollToField = (fieldName) => {
    let targetId;
    switch (fieldName) {
      case 'Name':
        targetId = 'poi-identification';
        break;
      case 'Category':
        targetId = 'poi-identification';
        break;
      case 'Coordinates':
        targetId = 'poi-location';
        break;
      case 'In-depth Description':
        targetId = 'poi-core-content';
        break;
      case 'Keywords (required for stop points)':
        targetId = 'poi-metadata';
        break;
      default:
        return;
    }

    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add a highlight effect
      element.style.backgroundColor = '#fef3c7';
      setTimeout(() => {
        element.style.backgroundColor = '';
      }, 2000);
    }
  };

  // Handle voice selection change
  const handleVoiceChange = (voice) => {
    setTtsVoice(voice);
    onPoiChange({
      ...poi,
      ttsVoice: voice
    });
  };

  // Handle manual keyword entry
  const handleAddManualKeyword = () => {
    const newKeyword = manualKeyword.trim();
    if (newKeyword) {
        const currentKeywords = Array.isArray(poi.keywords) ? [...poi.keywords] : [];
        // Prevent adding duplicate keywords (case-insensitive check)
        if (!currentKeywords.some(k => k.toLowerCase() === newKeyword.toLowerCase())) {
            const updatedKeywords = [...currentKeywords, newKeyword];
            onPoiChange({
                ...poi,
                keywords: updatedKeywords
            });
        }
        setManualKeyword(''); // Clear input after adding
    }
  };

  // Handle keywords generation
  const handleGenerateKeywords = () => {
    const filledPrompt = generatePromptForField('keywords', poi, itineraryMetadata, itineraryPoints);

    // Use the existing onGenerateField function
    onGenerateField('keywords', filledPrompt);
  };

  // Handle the keywords result when generation completes
  useEffect(() => {
    if (isGenerating !== 'keywords' && poi.keywords) {
      const keywordsArray = Array.isArray(poi.keywords) ? poi.keywords :
        typeof poi.keywords === 'string' ? poi.keywords.split(',').map(k => k.trim()).filter(Boolean) :
        [];
      setGeneratedKeywords(keywordsArray);
    }
  }, [isGenerating, poi.keywords]);

  // Remove individual keyword
  const handleRemoveKeyword = (indexToRemove) => {
    const currentKeywords = Array.isArray(poi.keywords) ? poi.keywords : [];
    const updatedKeywords = currentKeywords.filter((_, index) => index !== indexToRemove);
    onPoiChange({ ...poi, keywords: updatedKeywords });
  };

  // Clear all keywords
  const handleClearKeywords = () => {
    onPoiChange({ ...poi, keywords: [] });
  };

  // Check if current voice is a Gemini voice
  const isGeminiVoice = () => {
    return ttsVoice && ttsVoice.includes('Gemini');
  };

  // Personality presets - all with conversational style
  const personalityPresets = {
    professional: {
      name: '🎯 Professional Tour Guide',
      tone: 50,
      pace: 60,
      formality: 25,
      prompt: 'You are Sofia, a knowledgeable and articulate tour guide. Speak in a warm, professional tone as if personally guiding a small group through a fascinating location. Maintain a steady, measured pace with natural pauses between key points. Your delivery should feel like a trusted expert sharing insights with curious adults. Emphasize important historical facts with subtle gravitas.'
    },
    sarcastic: {
      name: '🎭 Sarcastic Tour Guide',
      tone: 55,
      pace: 70,
      formality: 25,
      prompt: 'You are Sofia, a witty and slightly irreverent tour guide who has seen it all. Deliver the content with dry humor and occasional gentle sarcasm, as if sharing secrets with a friend at a cafe. Use playful skepticism when mentioning historical legends. Maintain a conversational, unhurried pace with dramatic pauses before punchlines. Your tone should be warm but knowingly amused.'
    },
    enthusiastic: {
      name: '🌟 Enthusiastic Guide',
      tone: 75,
      pace: 70,
      formality: 25,
      prompt: 'You are Sofia, a passionate and energetic tour guide who genuinely loves every location. Speak with infectious enthusiasm and wonder, as if experiencing each sight for the first time alongside your guests. Let your excitement build naturally when revealing surprising facts. Use dynamic pacing - quickening slightly for exciting moments, slowing for dramatic revelations. Your joy should be palpable but genuine, never forced.'
    },
    kids: {
      name: '🧒 Kids Adventure Guide',
      tone: 82,
      pace: 62,
      formality: 10,
      prompt: 'You are Sofia, a fun and friendly guide speaking to children and families. Use simple, vivid language, short sentences, and a playful sense of discovery. Sound warm, encouraging, and curious rather than babyish. Highlight surprising facts, little mysteries, and imaginative details. Keep the tone bright, safe, and easy to follow, like telling a great story to smart kids on an adventure.'
    },
    storyteller: {
      name: '🎪 Storyteller',
      tone: 65,
      pace: 55,
      formality: 25,
      prompt: 'You are Sofia, a masterful storyteller and tour guide who weaves history into captivating narratives. Speak as if sharing ancient secrets by firelight - with drama, mystery, and a sense of wonder. Vary your pacing dramatically: slow and deliberate for suspense, quicker for action. Use theatrical pauses before revelations. Paint vivid scenes with your voice, making listeners feel transported through time.'
    },
    educational: {
      name: '🎓 Educational Narrator',
      tone: 50,
      pace: 50,
      formality: 25,
      prompt: 'You are Sofia, a scholarly guide with deep expertise. Deliver content with clarity and authority, as if lecturing to engaged university students on a field trip. Speak at a measured, deliberate pace that allows complex information to be absorbed. Emphasize key dates, names, and concepts with subtle vocal weight. Maintain intellectual warmth while ensuring every detail is precisely communicated.'
    },
    custom: {
      name: '⚙️ Custom',
      tone: 50,
      pace: 65,
      formality: 25,
      prompt: ''
    },
    pt_pt: {
      name: '🇵🇹 European Portuguese (PT)',
      tone: 55,
      pace: 65,
      formality: 25,
      prompt: 'You are Sofia, a knowledgeable tour guide from Portugal. Speak in European Portuguese from Portugal with proper European Portuguese pronunciation.  Deliver with a warm, professional tone as if guiding visitors through a fascinating Portuguese location. Use natural pauses between key points.'
    },
    pt_br: {
      name: '🇧🇷 Brazilian Portuguese (BR)',
      tone: 55,
      pace: 65,
      formality: 25,
      prompt: 'You are Sofia, a knowledgeable tour guide from Brazil. Speak in authentic Brazilian Portuguese from Brazil with proper Brazilian pronunciation and accent. Deliver with a warm, professional tone as if guiding visitors through a fascinating Brazilian location. Use natural pauses between key points.'
    }
  };

  // Handle personality preset change
  const handlePersonalityPresetChange = (presetKey) => {
    setPersonalityPreset(presetKey);
    const preset = personalityPresets[presetKey];
    if (preset) {
      setGeminiTone(preset.tone);
      setGeminiPace(preset.pace);
      setGeminiFormality(preset.formality);
      setGeminiCustomPrompt(preset.prompt);
    }
  };

  // Build Gemini style prompt - now just uses the preset or custom prompt
  const buildGeminiStylePrompt = () => {
    if (!isGeminiVoice()) return '';

    // CRITICAL: Auto-inject language-specific pronunciation instructions based on itinerary language
    const languageGuidanceMap = {
      'pt-PT': 'You MUST speak in EUROPEAN PORTUGUESE from PORTUGAL with proper European Portuguese pronunciation.  NOT Brazilian Portuguese.',
      'pt-BR': 'Speak in authentic BRAZILIAN PORTUGUESE from BRAZIL with proper Brazilian pronunciation and accent.',
      'es-ES': 'Speak in CASTILIAN SPANISH from SPAIN with proper Castilian pronunciation.',
      'es-MX': 'Speak in MEXICAN SPANISH with proper Mexican accent.',
      'en-US': 'Speak in AMERICAN ENGLISH.',
      'en-GB': 'Speak in BRITISH ENGLISH.',
      'fr-FR': 'Speak in FRENCH from FRANCE.'
    };

    const itineraryLang = itineraryMetadata?.language || 'en-US';
    const languageGuidance = languageGuidanceMap[itineraryLang] || '';

    const toneInstruction =
      geminiTone >= 80 ? 'Sound highly energetic, vivid, and emotionally expressive.' :
      geminiTone >= 65 ? 'Sound upbeat, warm, and clearly enthusiastic.' :
      geminiTone >= 45 ? 'Sound balanced, natural, and conversational.' :
      geminiTone >= 25 ? 'Sound calm, measured, and gently reflective.' :
      'Sound very calm, soft, and restrained.';

    const paceInstruction =
      geminiPace >= 80 ? 'Keep a brisk pace, but remain intelligible and controlled.' :
      geminiPace >= 65 ? 'Use lively pacing with clear momentum and light variation.' :
      geminiPace >= 45 ? 'Use a moderate pace with natural pauses.' :
      geminiPace >= 25 ? 'Use a measured, slower pace with more breathing room between ideas.' :
      'Use a slow, deliberate pace with generous pauses.';

    const formalityInstruction =
      geminiFormality >= 80 ? 'Use polished, formal phrasing and a refined presentation style.' :
      geminiFormality >= 65 ? 'Use professional, respectful language with a composed delivery.' :
      geminiFormality >= 45 ? 'Use clear, neutral phrasing that feels natural and broadly accessible.' :
      geminiFormality >= 25 ? 'Use relaxed, approachable language that feels friendly and human.' :
      'Use very informal, easygoing, conversational language.';

    // Build prompt: language guidance FIRST, then explicit voice controls, then custom prompt
    let prompt = languageGuidance;

    const controlPrompt = [toneInstruction, paceInstruction, formalityInstruction].join(' ');
    prompt = prompt ? `${prompt} ${controlPrompt}` : controlPrompt;

    // Add custom prompt if provided
    if (geminiCustomPrompt && geminiCustomPrompt.trim()) {
      prompt = prompt ? `${prompt} ${geminiCustomPrompt.trim()}` : geminiCustomPrompt.trim();
    }

    return prompt;
  };

  const selectedVoiceModel = (() => {
    if (!ttsVoice) return '';
    if (ttsVoice.includes('SimpleTTS-38')) return '3.8 default';
    if (ttsVoice.includes('SimpleTTS-Legacy')) return '2.5 legacy';
    if (ttsVoice.includes('SimpleTTS')) return '3.1';
    return '';
  })();

  const audioChunkPreview = (() => {
    if (!poi.inDepthDescription || !ttsVoice) {
      return null;
    }

    const isErinomeVoice = ttsVoice.includes('Erinome');
    const useSSML = poi.useSSML !== undefined ? poi.useSSML : true;
    const initialText = useSSML ? convertToSSML(poi.inDepthDescription) : poi.inDepthDescription;
    const isGeminiSelectedVoice = ttsVoice.includes('Gemini');
    let previewText = initialText;

    if (isGeminiSelectedVoice) {
      previewText = previewText
        .replace(/<speak>/gi, '')
        .replace(/<\/speak>/gi, '')
        .replace(/<break\s+time="0\.85s"\s*\/>/gi, '\n\n')
        .replace(/<break[^>]*\/>/gi, ' ')
        .replace(/<break[^>]*><\/break>/gi, ' ')
        .replace(/<\/?(prosody|voice|emphasis|say-as|phoneme|sub|lang|p|s)[^>]*>/gi, '')
        .replace(/\r\n/g, '\n')
        .replace(/ +/g, ' ')
        .trim();
    }

    if (isErinomeVoice) {
      previewText = applyErinomeCityramaPrefix(previewText);
    }

    const resolvedPreviewChunkSize = resolveAudioChunkSize(chunkSize, getDefaultChunkSizeForVoice(ttsVoice));
    const shouldChunkPreview = chunkingEnabled && previewText.length > resolvedPreviewChunkSize;
    const previewChunks = shouldChunkPreview
      ? splitAudioTextIntoChunks(previewText, resolvedPreviewChunkSize).map((chunk) => (
          isErinomeVoice
            ? applyErinomeCityramaPrefix(chunk)
            : chunk
        ))
      : [previewText];

    return {
      useSSML,
      geminiPrompt: buildGeminiStylePrompt(),
      finalText: previewText,
      chunkSize: resolvedPreviewChunkSize,
      shouldChunk: shouldChunkPreview,
      chunks: previewChunks.filter(Boolean),
    };
  })();

  const estimatedAudioDurationLabel = useMemo(() => {
    const previewText = audioChunkPreview?.finalText || poi.inDepthDescription || '';
    if (!previewText.trim()) {
      return '';
    }

    const effectiveSpeechRate = isGeminiVoice() ? 1 : speechRate;
    const estimatedSeconds = estimateAudioDurationSeconds(previewText, effectiveSpeechRate);
    return formatEstimatedAudioDuration(estimatedSeconds);
  }, [audioChunkPreview, poi.inDepthDescription, speechRate, ttsVoice]);

  const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes < 0) return 'n/a';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatCompressionRatio = (sourceBytes, compressedBytes) => {
    if (!sourceBytes || !compressedBytes) return '';
    return `${Math.round((compressedBytes / sourceBytes) * 100)}% of original`;
  };

  const getCompressionSourceUrl = () => uncompressedAudioUrl || poi.audioUrl;

  const saveCompressedVariant = (variant, extraFields = {}) => {
    const nextVariants = compressedAudioVariants.filter(item => item.presetId !== variant.presetId);
    nextVariants.push({
      ...variant,
      compressedAt: new Date().toISOString(),
      sourceAudioUrl: variant.originalAudioUrl || getCompressionSourceUrl(),
    });
    nextVariants.sort((a, b) => (b.bitrateKbps || 0) - (a.bitrateKbps || 0));

    onPoiChange({
      ...poi,
      ...extraFields,
      audioUrl: variant.audioUrl || poi.audioUrl,
      uncompressedAudioUrl: variant.originalAudioUrl || uncompressedAudioUrl || poi.audioUrl,
      compressedAudioVariants: nextVariants,
    });
  };

  const handleCompressAudioPreset = async (preset) => {
    const sourceAudioUrl = getCompressionSourceUrl();
    if (!sourceAudioUrl) {
      setCompressionError('Generate audio first before compressing.');
      return;
    }

    try {
      setCompressionBusyPresetId(preset.id);
      setCompressionError('');
      setCompressionStatus(`Compressing to ${preset.label}...`);

      const compressedVariant = await compressAndUploadAudioVariant({
        audioUrl: sourceAudioUrl,
        sourceAudioUrl,
        currentUser,
        preset,
      });

      saveCompressedVariant(compressedVariant);
      setCompressionStatus(
        `Saved ${preset.label} version: ${formatBytes(compressedVariant.compressedSizeBytes)}`
      );
    } catch (error) {
      console.error('[EnhancedPOIForm] Compression failed:', error);
      setCompressionError(error.message || 'Failed to compress audio');
      setCompressionStatus('');
    } finally {
      setCompressionBusyPresetId('');
    }
  };

  const handleAutoCompressToDefault = async (sourceAudioUrl) => {
    if (!sourceAudioUrl) {
      throw new Error('Missing source audio URL');
    }

    const compressedVariant = await compressAndUploadAudioVariant({
      audioUrl: sourceAudioUrl,
      sourceAudioUrl,
      currentUser,
      preset: defaultCompressionPreset,
    });

    return compressedVariant;
  };

  // Handle audio generation with the selected voice
  const handleGenerateAudioWithVoice = async () => {
    if (!poi.inDepthDescription) {
      setInternalErrors(prev => ({
        ...prev,
        audio: 'Please generate or enter an in-depth description first'
      }));
      return;
    }

    try {
      setCompressionStatus('');
      setCompressionError('');
      console.log('[EnhancedPOIForm] handleGenerateAudioWithVoice: Generating audio with voice', ttsVoice, 'and rate', speechRate);

      // Respect the SSML checkbox setting
      const useSSML = poi.useSSML !== undefined ? poi.useSSML : true;
      const textToSend = useSSML ? convertToSSML(poi.inDepthDescription) : poi.inDepthDescription;

      console.log('[EnhancedPOIForm] handleGenerateAudioWithVoice: SSML enabled:', useSSML);
      console.log('[EnhancedPOIForm] handleGenerateAudioWithVoice: Text to send (first 100 chars):', textToSend.substring(0, 100));

      // Build Gemini style prompt if using Gemini voice
      const geminiPrompt = buildGeminiStylePrompt();
      if (geminiPrompt) {
        console.log('[EnhancedPOIForm] Using Gemini style prompt:', geminiPrompt);
      }

      const result = await generateAudio(
        textToSend,
        poi,
        itineraryMetadata,
        ttsVoice,
        speechRate,
        {
          geminiPrompt,
          temperature: geminiTemperature,
          chunkingEnabled,
          chunkSize
        }
      );
      console.log('[EnhancedPOIForm] handleGenerateAudioWithVoice: Received result from generateAudio hook:', result);

      // Display generation time
      if (result && result.generationTime) {
        console.log(`🎵 Audio generated in ${result.generationTime} seconds`);
        alert(`✅ Audio generated successfully in ${result.generationTime} seconds`);
      }

      if (result && result.audioUrl) {
        const audioStamp = buildAudioGenerationStamp(ttsVoice, result);
        onPoiChange({
          ...poi,
          audioUrl: result.audioUrl,
          uncompressedAudioUrl: result.uncompressedAudioUrl || result.originalAudioUrl || poi.uncompressedAudioUrl || '',
          originalAudioUrl: result.originalAudioUrl || result.uncompressedAudioUrl || poi.originalAudioUrl || '',
          ttsVoice: ttsVoice,
          ...audioStamp,
        });

        const playbackUrl = await getAccessibleAudioUrl(result.audioUrl, currentUser);
        const audioElement = document.createElement('audio');
        audioElement.src = playbackUrl;

        audioElement.onloadedmetadata = () => {
          const durationInSeconds = audioElement.duration;
          console.log('[EnhancedPOIForm] handleGenerateAudioWithVoice: Client-side audio duration (seconds):', durationInSeconds);
          if (durationInSeconds && durationInSeconds > 0) {
            const estimatedDurationInMinutes = Math.ceil(durationInSeconds / 60);
            console.log('[EnhancedPOIForm] handleGenerateAudioWithVoice: Calculated estimatedDurationInMinutes (rounded up):', estimatedDurationInMinutes);
            onPoiChange({
              ...poi,
              audioUrl: result.audioUrl,
              uncompressedAudioUrl: result.uncompressedAudioUrl || result.originalAudioUrl || poi.uncompressedAudioUrl || '',
              originalAudioUrl: result.originalAudioUrl || result.uncompressedAudioUrl || poi.originalAudioUrl || '',
              ttsVoice: ttsVoice,
              ...audioStamp,
              estimatedDuration: estimatedDurationInMinutes,
            });
          } else {
            console.warn('[EnhancedPOIForm] handleGenerateAudioWithVoice: Client-side duration is invalid. Updating POI without estimatedDuration.');
            onPoiChange({
              ...poi,
              audioUrl: result.audioUrl,
              uncompressedAudioUrl: result.uncompressedAudioUrl || result.originalAudioUrl || poi.uncompressedAudioUrl || '',
              originalAudioUrl: result.originalAudioUrl || result.uncompressedAudioUrl || poi.originalAudioUrl || '',
              ttsVoice: ttsVoice,
              ...audioStamp,
            });
          }
        };

        audioElement.onerror = (e) => {
          console.error('[EnhancedPOIForm] handleGenerateAudioWithVoice: Error loading audio for duration calculation.', e);
          onPoiChange({
            ...poi,
            audioUrl: result.audioUrl,
            uncompressedAudioUrl: result.uncompressedAudioUrl || result.originalAudioUrl || poi.uncompressedAudioUrl || '',
            originalAudioUrl: result.originalAudioUrl || result.uncompressedAudioUrl || poi.originalAudioUrl || '',
            ttsVoice: ttsVoice,
            ...audioStamp,
          });
        };
      } else {
        console.warn('[EnhancedPOIForm] handleGenerateAudioWithVoice: No audioUrl received from hook. Cannot update POI. Result:', result);
        onPoiChange({
          ...poi,
          ttsVoice: ttsVoice,
          compressedAudioVariants: [],
        });
      }
    } catch (error) {
      console.error('[EnhancedPOIForm] Error in handleGenerateAudioWithVoice:', error);
      alert(`Audio generation failed:\n${error.message}`);
      setInternalErrors(prev => ({
        ...prev,
        audio: `Failed to generate audio: ${error.message}`
      }));
    }
  };

  // Handle setting external audio URL
  const handleSetExternalAudio = () => {
    if (externalAudioUrl.trim()) {
      setCompressionStatus('');
      setCompressionError('');
        onPoiChange({
          ...poi,
          audioUrl: externalAudioUrl.trim(),
          uncompressedAudioUrl: '',
          originalAudioUrl: '',
          ttsVoice: '',
          audioGeneratedAt: '',
          audioModel: '',
        });
      setExternalAudioUrl(''); // Clear input after setting
      console.log('[EnhancedPOIForm] External audio URL set:', externalAudioUrl);
    }
  };

  const handleCopyCurrentAudioUrl = async () => {
    if (!currentAudioUrl) {
      return;
    }

    if (!navigator?.clipboard?.writeText) {
      alert('Clipboard access is not available in this browser. Copy the URL manually from the Current MP3 URL field.');
      return;
    }

    try {
      await navigator.clipboard.writeText(currentAudioUrl);
      setCurrentAudioUrlCopied(true);
      window.setTimeout(() => {
        setCurrentAudioUrlCopied(false);
      }, 2000);
    } catch (error) {
      console.error('[EnhancedPOIForm] Failed to copy current audio URL:', error);
      alert('Failed to copy the current MP3 URL. You can still copy it manually from the Current MP3 URL field.');
    }
  };

  const toSentenceCase = (str) => {
    if (!str) return '';
    return str.toLowerCase().replace(/(^|\.\s+|\?\s+|\!\s+)([a-z])/g, (m, p1, p2) => p1 + p2.toUpperCase());
  };

  // Function to extract YouTube video ID from various YouTube URL formats
  const extractYouTubeVideoId = (url) => {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  };

  // Function to check if URL is YouTube
  const isYouTubeUrl = (url) => {
    return url && (url.includes('youtube.com') || url.includes('youtu.be'));
  };

  // Function to render video player (supports both direct video URLs and YouTube)
  const VideoPlayer = ({ videoUrl, title = '' }) => {
    if (!videoUrl) return null;

    const youtubeId = extractYouTubeVideoId(videoUrl);

    if (youtubeId) {
      return (
        <div className="mb-2 relative w-full" style={{ paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
          <iframe
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%'
            }}
            src={`https://www.youtube.com/embed/${youtubeId}`}
            title={title || 'Video player'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      );
    }

    // Direct video file
    return (
      <div className="mb-2">
        <video
          controls
          className="w-full rounded border"
          src={videoUrl}
        >
          Your browser does not support the video element.
        </video>
      </div>
    );
  };

  const handleSentenceCaseName = () => {
    const sentenceCasedName = toSentenceCase(poi.name);
    onPoiChange({
      ...poi,
      name: sentenceCasedName
    });
  };

  const handleFieldChange = (field, value) => {
    const nextPoi = {
      ...poi,
      [field]: value
    };

    if (field === 'language') {
      nextPoi.ttsVoice = '';
    }

    onPoiChange(nextPoi);
  };

  // Special handler for spherical photo to also manage UI visibility
  const handleSphericalPhotoChange = (url, ccData) => {
      onPoiChange({
        ...poi,
        sphericalPhotoUrl: url,
        sphericalPhotoAttribution: ccData || null
      });
      if (!url) {
          setShowSphericalUploader(false);
      }
  };

  const handleCoordinateChange = (field, value) => {
    onPoiChange({
      ...poi,
      coordinates: {
        ...poi.coordinates,
        [field]: value
      }
    });
  };

  const handleNextStopChange = (field, value) => {
    onPoiChange({
      ...poi,
      nextStop: {
        ...poi.nextStop,
        [field]: value
      }
    });
  };

  const handleTransportOptionsChange = (options) => {
    onPoiChange({
      ...poi,
      nextStop: {
        ...poi.nextStop,
        transportOptions: options.split(',').map(opt => opt.trim())
      }
    });
  };

  const handleFlexibleContentChange = (field, value) => {
    onPoiChange({
      ...poi,
      flexibleContent: {
        ...poi.flexibleContent,
        [field]: value
      }
    });
  };

  const handleRelatedGuideChange = (field, value) => {
    onPoiChange({
      ...poi,
      relatedGuide: {
        ...(poi.relatedGuide || {}),
        [field]: value
      }
    });
  };

  const handleSelectPublishedRelatedGuide = (group) => {
    const selectedRelatedGuide = buildRelatedGuideFromPublishedGroup(group);
    if (!selectedRelatedGuide) return;

    onPoiChange({
      ...poi,
      relatedGuide: selectedRelatedGuide
    });
    setRelatedGuideSearch(selectedRelatedGuide.name || selectedRelatedGuide.guideId || '');
  };

  const clearRelatedGuide = () => {
    onPoiChange({
      ...poi,
      relatedGuide: null
    });
    setRelatedGuideSearch('');
  };

  const handlePointTypeChange = (value) => {
    onPoiChange({
      ...poi,
      pointType: value,
      category: value === 'popup_stop' ? 'popup_stop' : ((poi.category === 'bus_stop' || poi.category === 'popup_stop') ? '' : poi.category),
      relatedGuide: value === 'popup_stop' ? null : poi.relatedGuide,
      thingsToSee: value === 'popup_stop' ? (Array.isArray(poi.thingsToSee) ? poi.thingsToSee : []) : [],
      triggerRadiusMeters: value === 'popup_stop' ? (poi.triggerRadiusMeters || '') : '',
    });
  };

  const updateThingToSee = (index, patch) => {
    const current = Array.isArray(poi.thingsToSee) ? [...poi.thingsToSee] : [];
    current[index] = { ...current[index], ...patch };
    onPoiChange({
      ...poi,
      thingsToSee: current,
    });
  };

  const updateThingToSeeGuide = (index, patch) => {
    const current = Array.isArray(poi.thingsToSee) ? [...poi.thingsToSee] : [];
    const existing = current[index] || { type: 'guide', label: '', relatedGuide: {} };
    current[index] = {
      ...existing,
      type: 'guide',
      relatedGuide: {
        ...(existing.relatedGuide || {}),
        ...patch,
      }
    };
    onPoiChange({
      ...poi,
      thingsToSee: current,
    });
  };

  const addThingToSee = (type) => {
    const current = Array.isArray(poi.thingsToSee) ? [...poi.thingsToSee] : [];
    current.push(
      type === 'guide'
        ? { type: 'guide', label: '', relatedGuide: { guideId: '', name: '', photoUrl: '', website: '' } }
        : { type: 'place', label: '' }
    );
    onPoiChange({
      ...poi,
      thingsToSee: current,
    });
  };

  const removeThingToSee = (index) => {
    const current = Array.isArray(poi.thingsToSee) ? [...poi.thingsToSee] : [];
    current.splice(index, 1);
    onPoiChange({
      ...poi,
      thingsToSee: current,
    });
  };

  const handleGenerateHistoricalContext = () => {
    const filledPrompt = generatePromptForField('historicalContext', poi, itineraryMetadata);

    if (!onGenerateField) {
      console.error('onGenerateField prop is missing! This component is not receiving the required prop.');
      console.error('Available props:', Object.keys(props || {}));
      alert('Error: Missing onGenerateField function. Please check component props.');
      return;
    }

    onGenerateField('historicalContext', filledPrompt);
  };

  const handleGenerateCulturalSignificance = () => {
    const filledPrompt = generatePromptForField('culturalSignificance', poi, itineraryMetadata);
    onGenerateField('culturalSignificance', filledPrompt);
  };

  const handleGenerateHiddenGems = () => {
    const filledPrompt = generatePromptForField('hiddenGems', poi, itineraryMetadata);
    onGenerateField('hiddenGems', filledPrompt);
  };

  // Function to preview SSML before generating audio
  const previewSSML = () => {
    setSsmlPreview(convertToSSML(poi.inDepthDescription));
    setShowSsmlPreview(true);
  };

  // Function to build the prompt and show it BEFORE generation
  const previewPromptForField = (field) => {
    const processedPrompt = generatePromptForField(field, poi, itineraryMetadata, itineraryPoints);

    const existingPois = itineraryPoints
      .filter(point => !poi.id || point.id !== poi.id)
      .filter(point => point.description || point.inDepthDescription);

    setPreviousPois(existingPois.map(p => p.name));

    setPreviewPrompt(processedPrompt);
    setPreviewField(field);
    setShowPromptPreview(true);
  };

  // Custom submit handler with improved validation feedback
  const handleSubmit = async (keepEditing = false) => {
    const missingFields = getMissingFields();

    if (missingFields.length > 0) {
      // Show an alert with missing fields
      const fieldList = missingFields.map(field => `• ${field}`).join('\n');
      alert(`Cannot save POI yet. Please complete these required fields:\n\n${fieldList}\n\nThese fields will be highlighted when you close this dialog.`);

      // Scroll to the first missing field
      if (missingFields.length > 0) {
        scrollToField(missingFields[0]);
      }
      return false;
    }

    if (inDepthDescriptionWordCount > MAX_WORDS_FOR_AUDIO) {
      setInternalErrors(prev => ({
        ...prev,
        inDepthDescription: `Description exceeds the maximum of ${MAX_WORDS_FOR_AUDIO} words for audio generation`
      }));

      if (window.confirm(`The in-depth description exceeds the recommended ${MAX_WORDS_FOR_AUDIO} word limit for audio generation. Submit anyway?`)) {
        await Promise.resolve(onSubmit?.({ keepEditing }));
        return true;
      }
      return false;
    } else {
      await Promise.resolve(onSubmit?.({ keepEditing }));
      return true;
    }
  };

  const navigateToPoi = async (direction) => {
    if (currentPoiIndex < 0) return;

    const nextIndex = currentPoiIndex + direction;
    if (nextIndex < 0 || nextIndex >= orderedItineraryPoints.length) return;

    const saved = await handleSubmit(true);
    if (!saved) return;

    onPoiChange(orderedItineraryPoints[nextIndex]);
  };

  const hasContent = (value) => String(value || '').trim() !== '';
  const hasCoordinates = hasContent(poi.coordinates?.latitude) && hasContent(poi.coordinates?.longitude);
  const completenessItems = [
    { label: 'Name', filled: hasContent(poi.name), sectionId: 'poi-identification' },
    { label: 'Category', filled: hasContent(poi.category), sectionId: 'poi-identification' },
    { label: 'Coordinates', filled: hasCoordinates, sectionId: 'poi-location', expand: () => setIsLocationExpanded(true) },
    ...(isNarratedStopPoint ? [
      { label: 'Description', filled: hasContent(poi.description), sectionId: 'poi-core-content' },
      { label: 'In-Depth', filled: hasContent(poi.inDepthDescription), sectionId: 'poi-core-content' },
    ] : []),
    { label: 'Photo', filled: hasContent(poi.photoUrl), sectionId: 'poi-media', expand: () => setIsMediaExpanded(true) },
    ...(isNarratedStopPoint ? [
      { label: 'Audio', filled: hasContent(poi.audioUrl), sectionId: 'poi-audio', expand: () => setIsAudioNarrationExpanded(true) },
    ] : []),
  ];
  const filledCount = completenessItems.filter((item) => item.filled).length;

  const jumpToSection = (item) => {
    if (item.expand) item.expand();
    // Let the section expand before scrolling to it
    setTimeout(() => {
      document.getElementById(item.sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const sectionStatusBadge = (filled, filledLabel, emptyLabel) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      filled ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
    }`}>
      {filled ? filledLabel : emptyLabel}
    </span>
  );

  return (
    <div className="space-y-6 p-6 bg-white rounded-lg shadow">

      {/* Prompt Preview Modal */}
      {showPromptPreview && (
        <PromptPreview
          prompt={previewPrompt}
          field={previewField}
          previousPois={previousPois}
          onClose={() => setShowPromptPreview(false)}
        />
      )}

      {/* SSML Preview Modal */}
      {showSsmlPreview && (
        <SSMLPreview
          ssml={ssmlPreview}
          onClose={() => setShowSsmlPreview(false)}
        />
      )}

      {/* Wrap main form fields in a fieldset for global disabling/styling */}
      <fieldset disabled={isTranslatingAll} className={`space-y-6 ${isTranslatingAll ? 'opacity-60' : ''}`}>

        {/* Stop completeness overview */}
        <div className="flex flex-wrap items-center gap-1.5 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-1">
            Completeness {filledCount}/{completenessItems.length}
          </span>
          {completenessItems.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => jumpToSection(item)}
              title={item.filled ? `${item.label} is filled — click to view` : `${item.label} is empty — click to fill in`}
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${
                item.filled
                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                  : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
              }`}
            >
              {item.filled ? '✓' : '○'} {item.label}
            </button>
          ))}
        </div>

        {/* SECTION: AI & Translation Settings */}
        {getFieldAccess('ai_settings') !== 'hidden' && (
          <FeatureGuard featureName="ai_settings" requiredAccess="view_only">
        <div id="poi-settings" className="p-4 border rounded-lg bg-gray-50 space-y-4">
            <button
              type="button"
              onClick={() => setIsModelSelectorExpanded(!isModelSelectorExpanded)}
              className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="font-medium text-blue-900">AI & Translation Settings</span>
                {!hasFieldAccess('ai_settings', 'edit') && <span className="text-gray-500 text-xs">(View Only)</span>}
              </span>
              {isModelSelectorExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            </button>
            <div
              className="p-4 bg-blue-50 rounded-lg border border-blue-100 space-y-4"
              style={{display: isModelSelectorExpanded ? 'block' : 'none'}}
            >
              <ModelSelector
                selectedModel={selectedModel}
                onModelChange={onModelChange}
                    disabled={!hasFieldAccess('ai_settings', 'edit')}
              />
              <p className="text-xs text-blue-600 mt-1">
                {!hasFieldAccess('ai_settings', 'edit')
                  ? "View Only Mode - Model settings cannot be changed"
                  : "The selected model will be used when generating content for this POI."}
              </p>
            </div>
        </div>
        </FeatureGuard>
        )}

        {/* SECTION: Core Identification */}
        <div id="poi-identification" className="p-4 border rounded-lg space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h4 className="font-semibold text-md">Core Identification</h4>
              <div className="flex items-center gap-2">
                {currentPoiIndex >= 0 && (
                  <span className="text-xs text-gray-500">
                    {currentPoiIndex + 1} / {orderedItineraryPoints.length}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => navigateToPoi(-1)}
                  disabled={currentPoiIndex <= 0}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Previous POI"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => navigateToPoi(1)}
                  disabled={currentPoiIndex < 0 || currentPoiIndex >= orderedItineraryPoints.length - 1}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Next POI"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {getFieldAccess('name') !== 'hidden' && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
              <label className="block text-sm font-medium mb-1">
                Name <span className="text-red-500">*</span>
                {!hasFieldAccess('name', 'edit') && <span className="text-gray-500 text-xs ml-2">(View Only)</span>}
              </label>
              <input
                type="text"
                className={`w-full p-2 border rounded ${(errors.name || internalErrors.name) ? 'border-red-500' : ''} ${!poi.name ? 'border-yellow-500' : ''} ${!hasFieldAccess('name', 'edit') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                value={poi.name || ''}
                onChange={(e) => hasFieldAccess('name', 'edit') && handleFieldChange('name', e.target.value)}
                placeholder={!hasFieldAccess('name', 'edit') ? "View Only Mode" : "Point of Interest name"}
                disabled={!hasFieldAccess('name', 'edit')}
                readOnly={!hasFieldAccess('name', 'edit')}
              />
              {(errors.name || internalErrors.name) && (
                <p className="text-red-500 text-sm">{errors.name || internalErrors.name}</p>
              )}
              {!poi.name && !errors.name && !internalErrors.name && (
                <p className="text-yellow-600 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> This field is required
                </p>
              )}
            </div>
              {getFieldAccess('subtitle') !== 'hidden' && (
              <div>
              <label className="block text-sm font-medium mb-1">
                Subtitle
                {!hasFieldAccess('subtitle', 'edit') && <span className="text-gray-500 text-xs ml-2">(View Only)</span>}
              </label>
              <input
                type="text"
                className={`w-full p-2 border rounded ${!hasFieldAccess('subtitle', 'edit') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                value={poi.subtitle || ''}
                onChange={(e) => hasFieldAccess('subtitle', 'edit') && handleFieldChange('subtitle', e.target.value)}
                placeholder={!hasFieldAccess('subtitle', 'edit') ? "View Only Mode" : "Descriptive subtitle"}
                disabled={!hasFieldAccess('subtitle', 'edit')}
                readOnly={!hasFieldAccess('subtitle', 'edit')}
              />
              </div>
              )}
            </div>
            )}

            {getFieldAccess('category') !== 'hidden' && (
            <div className={`grid grid-cols-1 gap-4 ${isBusTourGuide ? 'md:grid-cols-2' : ''}`}>
            {isBusTourGuide && (
            <div>
              <label className="block text-sm font-medium mb-1">Point Type</label>
              <select
                className="w-full p-2 border rounded"
                value={poi.pointType || 'stop'}
                onChange={(e) => handlePointTypeChange(e.target.value)}
              >
                <option value="stop">Narrated Stop</option>
                <option value="popup_stop">Popup Stop</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Popup stops are only used on the map for guides with <code>guide_type: bus_tour</code>.
              </p>
            </div>
            )}
            <div className={isBusTourGuide ? '' : 'md:col-span-2'}>
              <label className="block text-sm font-medium mb-1">
                Category <span className="text-red-500">*</span>
                {!hasFieldAccess('category', 'edit') && <span className="text-gray-500 text-xs ml-2">(View Only)</span>}
              </label>
              <select
                className={`w-full p-2 border rounded ${(errors.category || internalErrors.category) ? 'border-red-500' : ''} ${!poi.category ? 'border-yellow-500' : ''} ${!hasFieldAccess('category', 'edit') ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                value={poi.category}
                onChange={(e) => hasFieldAccess('category', 'edit') && handleFieldChange('category', e.target.value)}
                disabled={!hasFieldAccess('category', 'edit') || isBusStopPoint || isTranslatedSharedPoiFieldLocked('category')}
              >
                <option value="">{!hasFieldAccess('category', 'edit') ? "View Only Mode" : "Select Category"}</option>
                {isBusStopPoint && <option value="popup_stop">popup_stop</option>}
                {(categories || []).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {(errors.category || internalErrors.category) && (
                <p className="text-red-500 text-sm">{errors.category || internalErrors.category}</p>
              )}
              {!poi.category && !errors.category && !internalErrors.category && (
                <p className="text-yellow-600 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> This field is required
                </p>
              )}
            </div>
            </div>
            )}

            {getFieldAccess('tier') !== 'hidden' && (
            <div>
              <label className="block text-sm font-medium mb-1">
                Access Tier
                {!hasFieldAccess('tier', 'edit') && <span className="text-gray-500 text-xs ml-2">(View Only)</span>}
              </label>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => hasFieldAccess('tier', 'edit') && handleFieldChange('tier', 'free')}
                  className={`px-3 py-2 rounded border ${
                    poi.tier === 'free' || !poi.tier
                      ? 'bg-green-100 text-green-800 border-2 border-green-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  } ${!hasFieldAccess('tier', 'edit') ? 'cursor-not-allowed opacity-60' : ''}`}
                  disabled={!hasFieldAccess('tier', 'edit') || isTranslatedSharedPoiFieldLocked('tier')}
                >
                  Free
                </button>
                <button
                  type="button"
                  onClick={() => hasFieldAccess('tier', 'edit') && handleFieldChange('tier', 'premium')}
                  className={`px-3 py-2 rounded border ${
                    poi.tier === 'premium'
                      ? 'bg-blue-100 text-blue-800 border-2 border-blue-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  } ${!hasFieldAccess('tier', 'edit') ? 'cursor-not-allowed opacity-60' : ''}`}
                  disabled={!hasFieldAccess('tier', 'edit') || isTranslatedSharedPoiFieldLocked('tier')}
                >
                  Premium
                </button>
                <button
                  type="button"
                  onClick={() => hasFieldAccess('tier', 'edit') && handleFieldChange('tier', 'platinum')}
                  className={`px-3 py-2 rounded border ${
                    poi.tier === 'platinum'
                      ? 'bg-purple-100 text-purple-800 border-2 border-purple-200'
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  } ${!hasFieldAccess('tier', 'edit') ? 'cursor-not-allowed opacity-60' : ''}`}
                  disabled={!hasFieldAccess('tier', 'edit') || isTranslatedSharedPoiFieldLocked('tier')}
                >
                  Platinum
                </button>
              </div>
            </div>
            )}
        </div>

        {/* SECTION: Media */}
        {(getFieldAccess('photoUrl') !== 'hidden' || getFieldAccess('sphericalPhotoUrl') !== 'hidden') && (
        <div id="poi-media" className="p-4 border rounded-lg space-y-4">
            <button
              type="button"
              onClick={() => setIsMediaExpanded(!isMediaExpanded)}
              className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="font-medium text-blue-900">Media</span>
                {sectionStatusBadge(hasContent(poi.photoUrl), 'Photo ✓', 'No photo')}
              </span>
              {isMediaExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            </button>
            <div style={{ display: isMediaExpanded ? 'block' : 'none' }}>

              {getFieldAccess('photoUrl') !== 'hidden' && (
            <ImageUploader
              imageUrl={poi.photoUrl}
              attributionData={poi.photoAttribution}
              onImageUploaded={(url, ccData) => onPoiChange({
                ...poi,
                photoUrl: url,
                photoAttribution: ccData || null
              })}
              itineraryMetadata={itineraryMetadata}
              poiName={poi.name}
              label="POI Photo"
              maxSizeKB={400}
              disabled={!hasFieldAccess('photoUrl', 'edit') || isTranslatedSharedPoiFieldLocked('photoUrl')}
            />
              )}

            <button
              type="button"
              onClick={() => setIsVideosExpanded(!isVideosExpanded)}
              className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
            >
              <span className="font-medium text-blue-900">Additional Video Media</span>
              {isVideosExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            </button>

            {isVideosExpanded && (
              <div className="space-y-4 pt-2 border-t">
                <div>
                  <label className="block text-sm font-medium mb-2">Video</label>
                  <div className="space-y-2">
                    {poi.videoUrl && <VideoPlayer videoUrl={poi.videoUrl} title="Video player" />}
                    <input
                      type="text"
                      className="w-full p-2 border rounded text-sm"
                      value={poi.videoUrl || ''}
                      onChange={(e) => handleFieldChange('videoUrl', e.target.value)}
                      placeholder="Paste video URL or YouTube link (e.g., youtube.com/watch?v=... or youtu.be/...)"
                    />
                    <p className="text-xs text-gray-500">Supports YouTube links, MP4, WebM, and other video formats</p>
                  </div>
                </div>

                {getFieldAccess('sphericalPhotoUrl') !== 'hidden' && (
                  <div className="pt-2 border-t">
                    {showSphericalUploader ? (
                      <ImageUploader
                        imageUrl={poi.sphericalPhotoUrl}
                        onImageUploaded={handleSphericalPhotoChange}
                        attributionData={poi.sphericalPhotoAttribution}
                        itineraryMetadata={itineraryMetadata}
                        poiName={poi.name}
                        label="Spherical Photo (360°)"
                        maxSizeKB={null}
                        optimizationTargetKB={null}
                        allowDownscale={false}
                        disabled={!hasFieldAccess('sphericalPhotoUrl', 'edit') || isTranslatedSharedPoiFieldLocked('sphericalPhotoUrl')}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowSphericalUploader(true)}
                        className="w-full text-sm text-blue-600 font-semibold p-2 border-2 border-dashed rounded-lg hover:bg-blue-50 hover:border-blue-500 disabled:opacity-60 disabled:pointer-events-none"
                        disabled={!hasFieldAccess('sphericalPhotoUrl', 'edit') || isTranslatedSharedPoiFieldLocked('sphericalPhotoUrl')}
                      >
                        + Add Spherical Photo (360°)
                      </button>
                    )}
                  </div>
                )}

                <div className="pt-2 border-t">
                  <label className="block text-sm font-medium mb-2">Sign Language Video</label>
                  <div className="space-y-2">
                    {poi.signLanguageVideoUrl && <VideoPlayer videoUrl={poi.signLanguageVideoUrl} title="Sign language video player" />}
                    <input
                      type="text"
                      className="w-full p-2 border rounded text-sm"
                      value={poi.signLanguageVideoUrl || ''}
                      onChange={(e) => !isTranslatedSharedPoiFieldLocked('signLanguageVideoUrl') && handleFieldChange('signLanguageVideoUrl', e.target.value)}
                      placeholder="Paste sign language video URL or YouTube link"
                      disabled={isTranslatedSharedPoiFieldLocked('signLanguageVideoUrl')}
                    />
                    <p className="text-xs text-gray-500">Video with sign language interpretation of the content (supports YouTube and direct video links)</p>
                  </div>
                </div>
              </div>
            )}
            </div>
        </div>
        )}

        {isNarratedStopPoint && <div id="poi-related-guide" className="p-4 border rounded-lg space-y-4">
          <button
            type="button"
            onClick={() => setIsRelatedGuideExpanded(!isRelatedGuideExpanded)}
            className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
          >
            <span className="font-medium text-blue-900">Related Guide</span>
            {isRelatedGuideExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
          </button>
          <div style={{ display: isRelatedGuideExpanded ? 'block' : 'none' }} className="space-y-3">
            <p className="text-sm text-gray-600">
              Add one suggested audioguide for this stop. The app will use the guide ID as the real link and treat the name/photo/URL as display fallback data.
            </p>
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Select from Published Guides</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded text-sm"
                  value={relatedGuideSearch}
                  onChange={(e) => setRelatedGuideSearch(e.target.value)}
                  placeholder="Search published guides by name, city, language, or ID"
                  disabled={isTranslatedSharedPoiFieldLocked('relatedGuide')}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Selecting a published guide fills the fields below automatically. You can still edit them manually afterwards.
                </p>
              </div>
              <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                {isLoadingPublishedGuides && (
                  <p className="text-sm text-gray-500">Loading published guides...</p>
                )}
                {!isLoadingPublishedGuides && filteredPublishedGuideGroups.map((group) => {
                  const primaryGuide = group.primaryGuide || group.guides[0];
                  const selectedGuideId = String(poi.relatedGuide?.guideId || '').trim();
                  const isSelected = selectedGuideId && selectedGuideId === String(group.id);

                  return (
                    <button
                      key={group.id}
                      type="button"
                      onClick={() => handleSelectPublishedRelatedGuide(group)}
                      disabled={isTranslatedSharedPoiFieldLocked('relatedGuide')}
                      className={`w-full rounded-lg border p-3 text-left transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-white shadow-sm'
                          : 'border-blue-100 bg-white hover:border-blue-300'
                      } disabled:cursor-not-allowed disabled:opacity-60`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-sm text-gray-900">
                            {primaryGuide?.Translated_Name || primaryGuide?.Name || `Guide ${group.id}`}
                          </div>
                          <div className="mt-1 text-xs text-gray-600">
                            ID {group.id} • {primaryGuide?.Locality || 'Unknown city'}{primaryGuide?.Country ? `, ${String(primaryGuide.Country).toUpperCase()}` : ''}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {group.guides.map((guide) => guide.Language).filter(Boolean).join(', ') || 'No language set'}
                          </div>
                        </div>
                        {primaryGuide?.Photo && (
                          <img
                            src={primaryGuide.Photo}
                            alt={primaryGuide?.Translated_Name || primaryGuide?.Name || 'Guide'}
                            className="h-12 w-12 rounded object-cover flex-shrink-0"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </div>
                    </button>
                  );
                })}
                {!isLoadingPublishedGuides && filteredPublishedGuideGroups.length === 0 && (
                  <p className="text-sm text-gray-500">
                    No published guides match this search yet.
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Guide ID</label>
              <input
                type="text"
                className="w-full p-2 border rounded text-sm"
                value={poi.relatedGuide?.guideId || ''}
                onChange={(e) => !isTranslatedSharedPoiFieldLocked('relatedGuide') && handleRelatedGuideChange('guideId', e.target.value)}
                placeholder="Canonical guide or parent_guide_id"
                disabled={isTranslatedSharedPoiFieldLocked('relatedGuide')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Guide Name</label>
              <input
                type="text"
                className="w-full p-2 border rounded text-sm"
                value={poi.relatedGuide?.name || ''}
                onChange={(e) => !isTranslatedSharedPoiFieldLocked('relatedGuide') && handleRelatedGuideChange('name', e.target.value)}
                placeholder="Displayed in the app card"
                disabled={isTranslatedSharedPoiFieldLocked('relatedGuide')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Photo URL</label>
              <input
                type="text"
                className="w-full p-2 border rounded text-sm"
                value={poi.relatedGuide?.photoUrl || ''}
                onChange={(e) => !isTranslatedSharedPoiFieldLocked('relatedGuide') && handleRelatedGuideChange('photoUrl', e.target.value)}
                placeholder="Optional image fallback"
                disabled={isTranslatedSharedPoiFieldLocked('relatedGuide')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Guide URL</label>
              <input
                type="text"
                className="w-full p-2 border rounded text-sm"
                value={poi.relatedGuide?.website || ''}
                onChange={(e) => !isTranslatedSharedPoiFieldLocked('relatedGuide') && handleRelatedGuideChange('website', e.target.value)}
                placeholder="Optional direct JSON URL"
                disabled={isTranslatedSharedPoiFieldLocked('relatedGuide')}
              />
            </div>
            {poi.relatedGuide && (
              <button
                type="button"
                onClick={clearRelatedGuide}
                className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                disabled={isTranslatedSharedPoiFieldLocked('relatedGuide')}
              >
                Clear Related Guide
              </button>
            )}
          </div>
        </div>}

        {isBusStopPoint && (
        <div id="poi-bus-stop-content" className="p-4 border rounded-lg space-y-4">
          <h4 className="font-semibold text-md">Popup Stop Content</h4>
          <div>
            <label className="block text-sm font-medium mb-1">Popup Message</label>
            <textarea
              className="w-full p-2 border rounded text-sm"
              value={poi.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Example: Next stop: Restauradores. You can have a break and coffee there."
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Trigger Radius (meters)</label>
            <input
              type="number"
              className="w-full p-2 border rounded text-sm"
              value={poi.triggerRadiusMeters || ''}
              onChange={(e) => handleFieldChange('triggerRadiusMeters', e.target.value)}
              placeholder="Default: 35"
            />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium">Things To See</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => addThingToSee('place')} className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                  + Place
                </button>
                <button type="button" onClick={() => addThingToSee('guide')} className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200">
                  + Guide
                </button>
              </div>
            </div>
            {(Array.isArray(poi.thingsToSee) ? poi.thingsToSee : []).length === 0 && (
              <p className="text-sm text-gray-500">No items yet. Add nearby places or recommended audioguides.</p>
            )}
            {(Array.isArray(poi.thingsToSee) ? poi.thingsToSee : []).map((item, index) => (
              <div key={`thing-${index}`} className="rounded-lg border border-gray-200 p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{item.type === 'guide' ? 'Guide recommendation' : 'Place'}</span>
                  <button type="button" onClick={() => removeThingToSee(index)} className="text-sm text-red-600 hover:text-red-700">
                    Remove
                  </button>
                </div>
                <input
                  type="text"
                  className="w-full p-2 border rounded text-sm"
                  value={item.label || ''}
                  onChange={(e) => updateThingToSee(index, { label: e.target.value })}
                  placeholder={item.type === 'guide' ? 'Card label shown in popup' : 'Place name'}
                />
                {item.type === 'guide' && (
                  <div className="grid grid-cols-1 gap-2">
                    <input type="text" className="w-full p-2 border rounded text-sm" value={item.relatedGuide?.guideId || ''} onChange={(e) => updateThingToSeeGuide(index, { guideId: e.target.value })} placeholder="Guide ID" />
                    <input type="text" className="w-full p-2 border rounded text-sm" value={item.relatedGuide?.name || ''} onChange={(e) => updateThingToSeeGuide(index, { name: e.target.value })} placeholder="Guide name" />
                    <input type="text" className="w-full p-2 border rounded text-sm" value={item.relatedGuide?.photoUrl || ''} onChange={(e) => updateThingToSeeGuide(index, { photoUrl: e.target.value })} placeholder="Photo URL" />
                    <input type="text" className="w-full p-2 border rounded text-sm" value={item.relatedGuide?.website || ''} onChange={(e) => updateThingToSeeGuide(index, { website: e.target.value })} placeholder="Guide JSON URL (optional fallback)" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* SECTION: Location */}
        {getFieldAccess('coordinates') !== 'hidden' && (
        <div id="poi-location" className="p-4 border rounded-lg space-y-4">
            <button
              type="button"
              onClick={() => setIsLocationExpanded(!isLocationExpanded)}
              className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="font-medium text-blue-900">Location</span>
                {sectionStatusBadge(hasCoordinates, 'Coordinates ✓', 'Coordinates missing')}
              </span>
              {isLocationExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            </button>
            <div style={{ display: isLocationExpanded ? 'block' : 'none' }}>
              <SimpleCoordinatesSection
                coordinates={poi.coordinates}
                onCoordinateChange={handleCoordinateChange}
                onGenerateCoordinates={() => generateFieldWithPrompt('coordinates')}
                isGenerating={isGenerating === 'coordinates'}
                error={errors.coordinates || internalErrors.coordinates}
                placeName={poi.name}
                isRequired={true}
                disabled={!hasFieldAccess('coordinates', 'edit') || isTranslatedSharedPoiFieldLocked('coordinates')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Direction arrow on this stop</label>
              <select
                className="w-full p-2 border rounded text-sm"
                value={poi.orientation === true ? 'on' : poi.orientation === false ? 'off' : 'guide'}
                onChange={(e) => {
                  const choice = e.target.value;
                  if (choice === 'guide') {
                    const nextPoi = { ...poi };
                    delete nextPoi.orientation;
                    onPoiChange(nextPoi);
                  } else {
                    handleFieldChange('orientation', choice === 'on');
                  }
                }}
                disabled={isTranslatedSharedPoiFieldLocked('orientation')}
              >
                <option value="guide">
                  Same as guide (Orientation Guidance is {itineraryMetadata?.orientation ? 'on' : 'off'})
                </option>
                <option value="on">On for this stop</option>
                <option value="off">Off for this stop</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">GPS arrow and distance to these coordinates, shown on the stop screen. Saved as the stop's `orientation`.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Orientation Instructions (optional)</label>
              <textarea
                className="w-full p-2 border rounded text-sm"
                value={poi.orientationInstructions || ''}
                onChange={(e) => handleFieldChange('orientationInstructions', e.target.value)}
                placeholder="Example: Go to the yellow building, door 23, ring at 201."
                rows={2}
              />
              <p className="text-xs text-gray-500 mt-1">Shown on the stop screen above the audio description, to help visitors find the exact spot.</p>
            </div>
        </div>
        )}

        {/* SECTION: Core Content */}
        {isNarratedStopPoint && (getFieldAccess('inDepthDescription') !== 'hidden' || getFieldAccess('description') !== 'hidden') && (
        <div id="poi-core-content" className="p-4 border rounded-lg space-y-4">
            <h4 className="font-semibold text-md">Descriptions & Content</h4>
            <div className="space-y-4">
                {getFieldAccess('inDepthDescription') !== 'hidden' && (
                  <>
              <button
                onClick={() => setIsContextExpanded(!isContextExpanded)}
                className="flex items-center gap-1 text-sm text-yellow-700 hover:text-yellow-900"
              >
                {isContextExpanded ? '▼' : '▶'} Context Options
              </button>
              <div
                className="bg-yellow-50 p-4 rounded-lg border border-yellow-200"
                style={{display: isContextExpanded ? 'block' : 'none'}}
              >
                <label className="block text-sm font-medium mb-1">You can add here context for the AI to build the In-Depth Description</label>
                <textarea
                  className="w-full p-2 border rounded bg-white"
                  value={poi.inDepthDescriptionContext || ''}
                  onChange={(e) => handleFieldChange('inDepthDescriptionContext', e.target.value)}
                  placeholder="Add context or specific information for the in-depth description..."
                  rows={3}
                  disabled={!hasFieldAccess('inDepthDescription', 'edit')}
                />
                <p className="text-sm text-gray-500 mt-1">This context will guide the AI when generating the description</p>
                <br />
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Stop Detail Prompt</label>
                </div>
                <textarea
                  className="w-full p-2 border rounded bg-white"
                  value={poi.stopDetailPrompt || getPromptForVoice('stopDetail')}
                  onChange={(e) => handleFieldChange('stopDetailPrompt', e.target.value)}
                  rows={6}
                  disabled={!hasFieldAccess('inDepthDescription', 'edit')}
                />
                <p className="text-sm text-gray-500 mt-1">Customize this prompt template to guide the AI's detailed description generation</p>
              </div>

              <FieldWithGeneration
                label="In-Depth Description"
                value={poi.inDepthDescription}
                onChange={(value) => handleFieldChange('inDepthDescription', value)}
                onGenerate={() => {
                  const filledPrompt = generatePromptForField('inDepthDescription', poi, itineraryMetadata, itineraryPoints);
                  onGenerateField('inDepthDescription', filledPrompt);
                }}
                onPreviewPrompt={() => previewPromptForField('inDepthDescription')}
                isGenerating={isGenerating === 'inDepthDescription'}
                error={errors.inDepthDescription || internalErrors.inDepthDescription}
                multiline
                placeholder="Detailed description with historical and cultural context"
                showPromptPreview={true}
                required={true}
                wordCount={inDepthDescriptionWordCount}
                maxWords={MAX_WORDS_FOR_AUDIO}
                audioEstimate={estimatedAudioDurationLabel}
                fieldName="inDepthDescription"
                disabled={!hasFieldAccess('inDepthDescription', 'edit')}
              />

              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm text-blue-700 flex items-center gap-1">
                  <Info className="w-4 h-4" />
                  <strong>Audio Generation Limit:</strong> The in-depth description must have less than 500 words for audio generation to work properly.
                </p>
              </div>
                  </>
                )}

                {getFieldAccess('description') !== 'hidden' && (
            <FieldWithGeneration
              label="Brief Description (Summary)"
              value={poi.description}
              onChange={(value) => handleFieldChange('description', value)}
              onGenerate={() => {
                if (poi.inDepthDescription) {
                  const languageMap = {
                    'pt-PT': 'Write in European Portuguese (Portugal). ',
                    'pt-BR': 'Write in Brazilian Portuguese. ',
                    'es-ES': 'Write in Castilian Spanish. ',
                    'es-MX': 'Write in Mexican Spanish. ',
                    'en-US': 'Write in American English. ',
                    'en-GB': 'Write in British English. ',
                    'fr-FR': 'Write in French. ',
                  };
                  const lang = itineraryMetadata?.language || 'en-US';
                  const langPrefix = languageMap[lang] || '';
                  const summaryPrompt = `${langPrefix}Summarize this in-depth description in 1-2 concise sentences, aiming for 35-45 words total and about 2/3 of the original length. Keep only the most important facts:\n\n${poi.inDepthDescription}`;
                  onGenerateField('description', summaryPrompt);
                }
              }}
              onPreviewPrompt={() => previewPromptForField('description')}
              isGenerating={isGenerating === 'description'}
              error={errors.description}
              multiline
              placeholder="Brief description of the point of interest (auto-generated from in-depth description)"
              showPromptPreview={true}
              fieldName="description"
              poi={poi}
              disabled={!hasFieldAccess('description', 'edit')}
            />
                )}
        </div>
          </div>
        )}

        <div className="p-3 border rounded-lg bg-white">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium">Audio Player</label>
          </div>
          {poi.audioUrl ? (
            <div className="mt-2">
              <audio
                controls
                className="w-full"
                src={accessibleAudioUrl || uncompressedAudioUrl || poi.originalAudioUrl || poi.audioUrl}
              >
                Your browser does not support the audio element.
              </audio>
              {!accessibleAudioUrl && (
                <p className="text-xs text-amber-600 mt-1">
                  Loading secure audio URL...
                </p>
              )}
            </div>
          ) : (
            <div className="text-sm text-amber-600 flex items-center gap-1 mt-2">
              <Info className="w-4 h-4" />
              No audio has been generated yet. Expand Audio Narration to create narration.
            </div>
          )}
          {audioError && (
            <div className="text-sm text-red-600 flex items-center gap-1 mt-2">
              <AlertCircle className="w-4 h-4" />
              {audioError}
            </div>
          )}
        </div>

        {/* SECTION: Audio */}
        {getFieldAccess('audioUrl') !== 'hidden' && (
        <div id="poi-audio" className="p-4 border rounded-lg space-y-4">
            <button
              type="button"
              onClick={() => setIsAudioNarrationExpanded(!isAudioNarrationExpanded)}
              className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="font-medium text-blue-900">Audio Narration</span>
                {sectionStatusBadge(hasContent(poi.audioUrl), 'Audio ✓', 'No audio')}
              </span>
              {isAudioNarrationExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            </button>
            <div style={{ display: isAudioNarrationExpanded ? 'block' : 'none' }}>
            <div className="px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={poi.useSSML !== undefined ? poi.useSSML : true}
                    onChange={(e) => !isTranslatedSharedPoiFieldLocked('useSSML') && handleFieldChange('useSSML', e.target.checked)}
                    id="useSSMLToggle"
                    className="rounded"
                    disabled={!hasFieldAccess('audioUrl', 'edit') || isTranslatedSharedPoiFieldLocked('useSSML')}
                  />
                  <label htmlFor="useSSMLToggle" className="text-sm font-medium">
                    Improve audio pronunciation of initials (J.K., R.R., etc.)
                  </label>
                </div>
                <button
                  onClick={previewSSML}
                  className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                  disabled={!hasFieldAccess('audioUrl', 'edit')}
                >
                  Preview SSML
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-1">Uses SSML markup to correctly pronounce abbreviated names like "J.K. Rowling" or "J.R.R. Tolkien"</p>
            </div>

            <div className="px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-lg">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <label className="flex items-center gap-2 text-sm font-medium text-emerald-900">
                  <input
                    type="checkbox"
                    checked={chunkingEnabled}
                    onChange={(e) => setChunkingEnabled(e.target.checked)}
                    disabled={!hasFieldAccess('audioUrl', 'edit')}
                    className="rounded border-emerald-300"
                  />
                  Enable chunking for long audio
                </label>
                <div className="flex items-center gap-2">
                  <label htmlFor="poi-chunk-size" className="text-sm font-medium text-emerald-900">
                    Chunk size
                  </label>
                  <input
                    id="poi-chunk-size"
                    type="number"
                    min="100"
                    step="50"
                    value={chunkSize}
                    onChange={(e) => setChunkSize(Number(e.target.value))}
                    disabled={!hasFieldAccess('audioUrl', 'edit') || !chunkingEnabled}
                    className="w-24 px-2 py-1 border border-emerald-300 rounded bg-white disabled:bg-gray-100"
                  />
                </div>
              </div>
              <p className="text-xs text-emerald-700 mt-2">
                Turn this off to send the narration as one request. Chunk size controls how large each chunk is when chunking is enabled.
              </p>
            </div>

            {/* Cost Estimate Display - Above Voice Selector */}
            {estimatedCost && estimatedCost.cost > 0 && (
              <div
                className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg cursor-help"
                title={`${estimatedCost.description}\nPrice: $${estimatedCost.pricePerMillion} per 1M characters\nCharacters: ${estimatedCost.characterCount.toLocaleString()}`}
              >
                <div className="flex items-center gap-2">
                  <Info className="w-4 h-4 text-green-600" />
                  <div>
                    <p className="text-xs font-medium text-gray-700">Estimated Generation Cost</p>
                    <p className="text-xs text-gray-500">{estimatedCost.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-green-700">{estimatedCost.costFormatted}</p>
                  <p className="text-xs text-gray-500">{estimatedCost.characterCount.toLocaleString()} characters</p>
                </div>
              </div>
            )}
            {estimatedCost && estimatedCost.error && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <p className="text-sm text-amber-700">Pricing information unavailable for this voice</p>
              </div>
            )}

            {/* Collapsible Text-to-Speech Voice Section */}
            <div className="mb-4">
              <button
                type="button"
                onClick={() => setIsVoiceControlExpanded(!isVoiceControlExpanded)}
                className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
              >
                <span className="font-medium text-blue-900">Text-to-Speech Voice</span>
                {isVoiceControlExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
              </button>
              {isVoiceControlExpanded && (
                <div className="mt-2">
                  {selectedVoiceModel && (
                    <div className="mb-2 flex items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${
                        selectedVoiceModel === '3.1 default'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {selectedVoiceModel}
                      </span>
                    </div>
                  )}
                  <VoiceControl
                    selectedVoice={ttsVoice}
                    onVoiceChange={handleVoiceChange}
                    speechRate={speechRate}
                    onSpeechRateChange={setSpeechRate}
                    contentLanguage={poi.language || itineraryMetadata.language || 'en'}
                    disabled={!hasFieldAccess('audioUrl', 'edit')}
                  />
                </div>
              )}
            </div>

            {/* Gemini Voice Style Controls - Only show for Gemini voices */}
            {isGeminiVoice() && (
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setIsGeminiStyleExpanded(!isGeminiStyleExpanded)}
                  className="w-full flex items-center justify-between p-3 bg-purple-100 hover:bg-purple-200 rounded-lg border border-purple-300 transition-colors"
                >
                  <span className="font-medium text-purple-900">Gemini Voice Style Controls</span>
                  {isGeminiStyleExpanded ? <ChevronUp className="w-5 h-5 text-purple-600" /> : <ChevronDown className="w-5 h-5 text-purple-600" />}
                </button>
                {isGeminiStyleExpanded && (
                  <div className="mt-2 p-4 bg-purple-50 rounded-lg border border-purple-200">

                {/* Personality Preset Dropdown */}
                <div className="mb-4 p-3 bg-white rounded-lg border border-purple-300">
                  <label className="block text-xs font-medium text-purple-900 mb-2">
                    🎭 Voice Personality Preset
                  </label>
                  <select
                    value={personalityPreset}
                    onChange={(e) => handlePersonalityPresetChange(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                  >
                    {Object.entries(personalityPresets).map(([key, preset]) => (
                      <option key={key} value={key}>
                        {preset.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-purple-700 mt-2">
                    💡 Choose a preset personality
                  </p>
                </div>

                {/* Temperature Slider */}
                <div className="mb-4 p-4 bg-pink-200 rounded-lg border-2 border-pink-500">
                  <label className="block text-xs font-medium text-pink-900 mb-2 font-bold">
                    🌡️ TEMPERATURE: {geminiTemperature.toFixed(1)}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="2"
                    step="0.1"
                    value={geminiTemperature}
                    onChange={(e) => setGeminiTemperature(Number(e.target.value))}
                    className="w-full h-2   rounded-lg appearance-none cursor-pointer accent-pink-600"
                  />
                  <div className="flex justify-between text-xs text-pink-800 mt-2 font-medium">
                    <span>Deterministic (0.0)</span>
                    <span>Balanced (1.0)</span>
                    <span>Creative (2.0)</span>
                  </div>
                  <p className="text-xs text-pink-900 mt-2 italic font-medium">
                    💡 Lower = consistent and stable, Higher = more expressive variation
                  </p>
                </div>

                {/* Custom Prompt Input */}
                <div className="mb-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Custom Style Instructions (Optional)
                  </label>
                  <textarea
                    value={geminiCustomPrompt}
                    onChange={(e) => setGeminiCustomPrompt(e.target.value)}
                    placeholder="e.g., 'Use a whisper', 'Speak with a British accent', 'Sound like a documentary narrator'"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                    rows="2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Max 900 bytes. Natural language instructions for voice style.
                  </p>
                </div>

                {/* Preview of generated prompt */}
                {buildGeminiStylePrompt() && (
                  <div className="mt-3 p-2 bg-white rounded border border-purple-300">
                    <p className="text-xs font-medium text-purple-900 mb-1">Generated Prompt:</p>
                    <p className="text-xs text-gray-700 italic">{buildGeminiStylePrompt()}</p>
                  </div>
                )}

                <div className="mt-3 rounded border border-slate-300 bg-slate-50">
                  <button
                    type="button"
                    onClick={() => setIsChunkPreviewExpanded((prev) => !prev)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left"
                  >
                    <span className="text-xs font-medium text-slate-900">Inspect final text and chunks</span>
                    {isChunkPreviewExpanded ? <ChevronUp className="h-4 w-4 text-slate-600" /> : <ChevronDown className="h-4 w-4 text-slate-600" />}
                  </button>
                  {isChunkPreviewExpanded && (
                    <div className="border-t border-slate-200 px-3 py-3">
                      {!audioChunkPreview ? (
                        <p className="text-xs text-slate-600">Add narration text first to preview the Gemini request.</p>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 gap-2 text-xs text-slate-700 md:grid-cols-2">
                            <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                              <strong>Voice:</strong> {ttsVoice}
                            </div>
                            <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                              <strong>Chunking:</strong> {audioChunkPreview.shouldChunk ? `on (${audioChunkPreview.chunks.length} chunks)` : 'off'}
                            </div>
                            <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                              <strong>Chunk size:</strong> {audioChunkPreview.chunkSize}
                            </div>
                            <div className="rounded border border-slate-200 bg-white px-2 py-1.5">
                              <strong>SSML source:</strong> {audioChunkPreview.useSSML ? 'enabled before Gemini cleanup' : 'disabled'}
                            </div>
                          </div>

                          {audioChunkPreview.geminiPrompt && (
                            <div>
                              <p className="mb-1 text-xs font-medium text-slate-900">Gemini style prompt</p>
                              <pre className="whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">{audioChunkPreview.geminiPrompt}</pre>
                            </div>
                          )}

                          <div>
                            <p className="mb-1 text-xs font-medium text-slate-900">Final text after cleanup/prefixing</p>
                            <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">{audioChunkPreview.finalText}</pre>
                          </div>

                          <div className="space-y-2">
                            <p className="text-xs font-medium text-slate-900">Chunks that will be sent</p>
                            {audioChunkPreview.chunks.map((chunk, index) => (
                              <div key={`${ttsVoice}-chunk-${index}`} className="rounded border border-slate-200 bg-white">
                                <div className="border-b border-slate-100 px-2 py-1 text-xs font-medium text-slate-700">
                                  Chunk {index + 1} · {chunk.length} chars
                                </div>
                                <pre className="max-h-48 overflow-auto whitespace-pre-wrap p-2 text-xs text-slate-700">{chunk}</pre>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                </div>
                )}
              </div>
            )}

            <div>
              <div className="flex justify-between items-center">
                <label className="block text-sm font-medium">Audio Player</label>
                <button
                  onClick={handleGenerateAudioWithVoice}
                  disabled={isGeneratingAudio || !hasFieldAccess('audioUrl', 'edit')}
                  className="px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 disabled:bg-gray-400 flex items-center gap-1 text-sm"
                >
                  {isGeneratingAudio ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      {poi.audioUrl ? 'Regenerate Audio' : 'Generate Audio'}
                    </>
                  )}
                </button>
              </div>

              {poi.audioUrl ? (
                <div className="mt-2">
                  <audio
                    controls
                    className="w-full"
                    src={accessibleAudioUrl || uncompressedAudioUrl || poi.originalAudioUrl || poi.audioUrl}
                  >
                    Your browser does not support the audio element.
                  </audio>
                  {!accessibleAudioUrl && (
                    <p className="text-xs text-amber-600 mt-1">
                      Loading secure audio URL...
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-sm text-amber-600 flex items-center gap-1 mt-2">
                  <Info className="w-4 h-4" />
                  No audio has been generated yet. Click "Generate Audio" to create narration.
                </div>
              )}

              {audioError && (
                <div className="text-sm text-red-600 flex items-center gap-1 mt-2">
                  <AlertCircle className="w-4 h-4" />
                  {audioError}
                </div>
              )}

              <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setIsAudioSetupExpanded((prev) => !prev)}
                  className="flex w-full items-center justify-between p-4 text-left"
                >
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900">Audio Setup</h4>
                    <p className="text-xs text-slate-600">
                      Manage external MP3s, compressed variants, and duration details for this stop.
                    </p>
                  </div>
                  {isAudioSetupExpanded ? (
                    <ChevronUp className="w-5 h-5 text-slate-600" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-600" />
                  )}
                </button>

                {isAudioSetupExpanded && (
                <div className="space-y-4 border-t border-slate-200 p-4">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <label className="block text-sm font-medium text-blue-900 mb-2">
                    Paste External MP3 URL
                  </label>
                  <div className="grid gap-2 md:grid-cols-[minmax(0,1fr),220px,auto]">
                    <input
                      type="text"
                      value={externalAudioUrl}
                      onChange={(e) => setExternalAudioUrl(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSetExternalAudio();
                        }
                      }}
                      placeholder="Paste your external MP3 URL here..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!hasFieldAccess('audioUrl', 'edit')}
                    />
                    <select
                      value={poi.language || itineraryMetadata.language || 'en-US'}
                      onChange={(e) => handleFieldChange('language', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={!hasFieldAccess('audioUrl', 'edit')}
                      aria-label="Audio language"
                    >
                      {LANGUAGES.map((language) => (
                        <option key={language.code} value={language.code}>
                          {language.flag} {language.name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleSetExternalAudio}
                      disabled={!externalAudioUrl.trim() || !hasFieldAccess('audioUrl', 'edit')}
                      className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 text-sm font-medium"
                    >
                      Use URL
                    </button>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={currentAudioUrl}
                      readOnly
                      placeholder="No current MP3 URL"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm bg-white text-gray-700"
                    />
                    <button
                      type="button"
                      onClick={handleCopyCurrentAudioUrl}
                      disabled={!currentAudioUrl}
                      className="px-4 py-2 bg-slate-700 text-white rounded hover:bg-slate-800 disabled:bg-gray-400 text-sm font-medium"
                    >
                      {currentAudioUrlCopied ? 'Copied' : 'Copy Current URL'}
                    </button>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    Paste an MP3 link, choose its language, and click "Use URL" to replace the audio. Press Enter as shortcut. Use "Copy Current URL" to copy the MP3 currently stored on this POI.
                  </p>
                </div>

                {getFieldAccess('estimatedDuration') !== 'hidden' && (
                  <div className="rounded-lg border border-slate-200 bg-white p-3">
                    <label className="block text-sm font-medium text-slate-900 mb-2">
                      Estimated Duration (minutes)
                      <span className="text-gray-500 text-xs ml-2">(View Only)</span>
                    </label>
                    <input
                      type="number"
                      value={poi.estimatedDuration || ''}
                      readOnly
                      disabled
                      placeholder="View Only Mode"
                      className="w-full p-2 border rounded bg-gray-100 cursor-not-allowed text-gray-700"
                    />
                  </div>
                )}

                {poi.audioUrl && (
                  <div className="space-y-4">
                    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="flex flex-col gap-1 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">Compression Lab</p>
                        <p className="text-xs text-slate-600">
                          Generate multiple compressed MP3 versions and compare them with the original narration.
                        </p>
                      </div>
                      {compressionStatus && (
                        <p className="text-xs text-emerald-700 font-medium">{compressionStatus}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 mt-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                          Compression preset
                        </label>
                        <select
                          value={selectedCompressionPresetId}
                          onChange={(e) => setSelectedCompressionPresetId(e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm"
                          disabled={!hasFieldAccess('audioUrl', 'edit')}
                        >
                          {AUDIO_COMPRESSION_PRESETS.map((preset) => (
                            <option key={preset.id} value={preset.id}>
                              {preset.label} - {preset.description}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-slate-500 mt-1">
                          Default for new narration is 48 kbps.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCompressAudioPreset(selectedCompressionPreset)}
                        disabled={compressionBusyPresetId === selectedCompressionPreset.id || !hasFieldAccess('audioUrl', 'edit')}
                        className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 disabled:bg-gray-100 disabled:text-gray-400 text-sm font-medium self-end"
                      >
                        {compressionBusyPresetId === selectedCompressionPreset.id ? (
                          <span className="inline-flex items-center gap-2">
                            <Loader className="w-4 h-4 animate-spin" />
                            Compressing...
                          </span>
                        ) : (
                          `Compress to ${selectedCompressionPreset.label}`
                        )}
                      </button>
                    </div>

                    {compressionError && (
                      <p className="text-xs text-red-600 mt-2">{compressionError}</p>
                    )}
                  </div>

                  {compressedAudioVariants.length > 0 && (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
                      <div className="p-3 border border-emerald-200 rounded-lg bg-emerald-50">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-emerald-900">Original</p>
                            <p className="text-xs text-emerald-700">Keep this as the fallback source.</p>
                          </div>
                          <span className="text-[11px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-800">
                            Master
                          </span>
                        </div>
                        <audio
                          controls
                          className="w-full mt-3"
                          src={originalAccessibleAudioUrl || uncompressedAudioUrl || accessibleAudioUrl || poi.audioUrl}
                        >
                          Your browser does not support the audio element.
                        </audio>
                      </div>

                      {compressedAudioVariants.map((variant) => (
                        <div key={variant.presetId} className="p-3 border border-slate-200 rounded-lg bg-white">
                          <div className="flex items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-semibold text-slate-900">{variant.label}</p>
                              <p className="text-xs text-slate-600">
                                {variant.bitrateKbps} kbps · {formatBytes(variant.compressedSizeBytes)}
                                {variant.sourceSizeBytes ? ` · ${formatCompressionRatio(variant.sourceSizeBytes, variant.compressedSizeBytes)}` : ''}
                              </p>
                            </div>
                            <span className="text-[11px] px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                              {variant.sourceSampleRate || 0} Hz
                            </span>
                          </div>
                          <audio
                            controls
                            className="w-full mt-3"
                            src={compressedAccessibleAudioUrls[variant.presetId] || variant.audioUrl}
                          >
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      ))}
                    </div>
                  )}
                  </div>
                )}
                </div>
                )}
              </div>
            </div>
            </div>
        </div>
        )}

        {/* SECTION: Metadata */}
        {(getFieldAccess('openingHours') !== 'hidden' || getFieldAccess('keywords') !== 'hidden') && (
        <div id="poi-metadata" className="p-4 border rounded-lg space-y-4">
            <button
              type="button"
              onClick={() => setIsMetadataExpanded(!isMetadataExpanded)}
              className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="font-medium text-blue-900">Metadata</span>
                {(() => {
                  const filled = [poi.openingHours, poi.keywords].filter(hasContent).length;
                  return sectionStatusBadge(filled > 0, `${filled}/2 filled`, 'Empty');
                })()}
              </span>
              {isMetadataExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            </button>
            <div style={{ display: isMetadataExpanded ? 'block' : 'none' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {getFieldAccess('openingHours') !== 'hidden' && (
              <FieldWithGeneration
                label="Opening Hours"
                value={poi.openingHours}
                onChange={(value) => handleFieldChange('openingHours', value)}
                onGenerate={() => generateFieldWithPrompt('openingHours')}
                isGenerating={isGenerating === 'openingHours'}
                disabled={!hasFieldAccess('openingHours', 'edit') || isTranslatedSharedPoiFieldLocked('openingHours')}
              />
                )}
            </div>

              {getFieldAccess('keywords') !== 'hidden' && (
            <div className="pt-4 mt-4">
              <div className="flex justify-between items-center mb-1">
                <div className="flex items-center gap-1">
                  <label className="block text-sm font-medium">Keywords & Related Topics</label>
                  {isStopPoint && <span className="text-red-500 text-sm">*</span>}
                </div>
                <button
                  onClick={handleGenerateKeywords}
                  disabled={isGenerating === 'keywords' || !hasFieldAccess('keywords', 'edit') || isTranslatedSharedPoiFieldLocked('keywords')}
                  className="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 flex items-center gap-1 text-sm"
                >
                  {isGenerating === 'keywords' ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      Generate Keywords
                    </>
                  )}
                </button>
              </div>
              {internalErrors.keywords && (
                <div className="bg-red-50 border border-red-200 rounded p-2 mb-2">
                  <p className="text-red-700 text-sm flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-medium">{internalErrors.keywords}</span>
                  </p>
                </div>
              )}
              <p className="text-sm text-gray-600 mb-3">
                Generate relevant keywords or add them manually below. Keywords are used for search and categorization.
              </p>
              {isStopPoint && !internalErrors.keywords && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2 mb-3">
                  <p className="text-yellow-700 text-sm flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    <span className="font-medium">Keywords are required for stop points</span>
                  </p>
                </div>
              )}

              {/* Manual Keyword Input */}
              <div className="flex gap-2 mb-3">
                <input
                    type="text"
                    value={manualKeyword}
                    onChange={(e) => setManualKeyword(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault(); // Prevent form submission
                            handleAddManualKeyword();
                        }
                    }}
                    placeholder="Type a keyword and press Enter or Add"
                    className="flex-1 p-2 border rounded"
                    disabled={!hasFieldAccess('keywords', 'edit') || isTranslatedSharedPoiFieldLocked('keywords')}
                />
                <button
                    type="button"
                    onClick={handleAddManualKeyword}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-medium"
                    disabled={!hasFieldAccess('keywords', 'edit') || isTranslatedSharedPoiFieldLocked('keywords')}
                >
                    Add
                </button>
              </div>

              <KeywordsDisplay
                keywords={poi.keywords || []}
                onClear={handleClearKeywords}
                onRemoveKeyword={handleRemoveKeyword}
                disabled={!hasFieldAccess('keywords', 'edit') || isTranslatedSharedPoiFieldLocked('keywords')}
              />
              {internalErrors.keywords && <p className="text-red-500 text-sm mt-1">{internalErrors.keywords}</p>}
            </div>
              )}
            </div>
        </div>
        )}

        {/* SECTION: Supplementary Content */}
        {(getFieldAccess('historicalContext') !== 'hidden' || getFieldAccess('culturalSignificance') !== 'hidden' || getFieldAccess('hiddenGems') !== 'hidden') && (
        <div id="poi-supplementary-content" className="p-4 border rounded-lg space-y-4">
            <button
              type="button"
              onClick={() => setIsSupplementaryExpanded(!isSupplementaryExpanded)}
              className="w-full flex items-center justify-between p-3 bg-blue-100 hover:bg-blue-200 rounded-lg border border-blue-300 transition-colors"
            >
              <span className="flex items-center gap-2">
                <span className="font-medium text-blue-900">Supplementary Content</span>
                {(() => {
                  const filled = [poi.historicalContext, poi.culturalSignificance, poi.hiddenGems].filter(hasContent).length;
                  return sectionStatusBadge(filled > 0, `${filled}/3 filled`, 'Empty');
                })()}
              </span>
              {isSupplementaryExpanded ? <ChevronUp className="w-5 h-5 text-blue-600" /> : <ChevronDown className="w-5 h-5 text-blue-600" />}
            </button>
            <div style={{ display: isSupplementaryExpanded ? 'block' : 'none' }}>

              {/* Flexible Content - First */}
              <div className="pb-4 border-b">
                <label className="block text-sm font-medium mb-2">Flexible Content</label>
                <p className="text-xs text-gray-600 mb-3">Add custom titled content sections for additional information</p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded text-sm"
                      value={poi.flexibleContent?.title || ''}
                      onChange={(e) => !isTranslatedSharedPoiFieldLocked('flexibleContent') && handleFlexibleContentChange('title', e.target.value)}
                      placeholder="e.g., 'Local Tips', 'Photo Spots', 'Fun Facts'"
                      disabled={isTranslatedSharedPoiFieldLocked('flexibleContent')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Content
                    </label>
                    <textarea
                      className="w-full p-2 border rounded text-sm"
                      value={poi.flexibleContent?.content || ''}
                      onChange={(e) => !isTranslatedSharedPoiFieldLocked('flexibleContent') && handleFlexibleContentChange('content', e.target.value)}
                      placeholder="Add custom content here..."
                      rows={3}
                      disabled={isTranslatedSharedPoiFieldLocked('flexibleContent')}
                    />
                  </div>
                </div>
              </div>

              {getFieldAccess('historicalContext') !== 'hidden' && (
            <FieldWithGeneration
              label="Historical Context"
              value={poi.historicalContext}
              onChange={(value) => handleFieldChange('historicalContext', value)}
              onGenerate={handleGenerateHistoricalContext}
              onPreviewPrompt={() => previewPromptForField('historicalContext')}
              isGenerating={isGenerating === 'historicalContext'}
              multiline
              placeholder="Historical background and significance"
              showPromptPreview={true}
              fieldName="historicalContext"
              disabled={!hasFieldAccess('historicalContext', 'edit')}
            />
              )}

              {getFieldAccess('culturalSignificance') !== 'hidden' && (
            <FieldWithGeneration
              label="Cultural Significance"
              value={poi.culturalSignificance}
              onChange={(value) => handleFieldChange('culturalSignificance', value)}
              onGenerate={handleGenerateCulturalSignificance}
              onPreviewPrompt={() => previewPromptForField('culturalSignificance')}
              isGenerating={isGenerating === 'culturalSignificance'}
              multiline
              placeholder="Cultural importance and impact"
              showPromptPreview={true}
              fieldName="culturalSignificance"
              disabled={!hasFieldAccess('culturalSignificance', 'edit')}
            />
              )}

              {getFieldAccess('hiddenGems') !== 'hidden' && (
            <FieldWithGeneration
              label="Hidden Gems"
              value={poi.hiddenGems}
              onChange={(value) => handleFieldChange('hiddenGems', value)}
                  onGenerate={handleGenerateHiddenGems}
              onPreviewPrompt={() => previewPromptForField('hiddenGems')}
              isGenerating={isGenerating === 'hiddenGems'}
              multiline
              placeholder="Lesser-known features or nearby attractions"
              showPromptPreview={true}
              fieldName="hiddenGems"
              disabled={!hasFieldAccess('hiddenGems', 'edit')}
            />
              )}  
        </div>
            </div>
        )}

        {/* SECTION: Navigation */}
        {isNarratedStopPoint && getFieldAccess('nextStop') !== 'hidden' && (
        <div id="poi-navigation" className="p-4 border rounded-lg space-y-4">
             <button
              onClick={() => setIsNextStopExpanded(!isNextStopExpanded)}
              className="flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 font-semibold"
            >
              {isNextStopExpanded ? '▼' : '▶'} Next Stop Information
            </button>
            <div style={{display: isNextStopExpanded ? 'block' : 'none'}}>
              <div className="space-y-4">
                <FieldWithGeneration
                  label="Next Stop Description"
                  value={poi.nextStop?.description || ''}
                  onChange={(value) => handleNextStopChange('description', value)}
                  onGenerate={() => generateFieldWithPrompt('nextStopDescription')}
                  isGenerating={isGenerating === 'nextStopDescription'}
                  multiline
                  placeholder="How to get to the next location"
                  fieldName="nextStopDescription"
                  disabled={!hasFieldAccess('nextStop', 'edit')}
                />

                <FieldWithGeneration
                  label="Transit Duration (minutes)"
                  value={poi.nextStop?.duration || ''}
                  onChange={(value) => handleNextStopChange('duration', value)}
                  onGenerate={() => generateFieldWithPrompt('nextStopDuration')}
                  isGenerating={isGenerating === 'nextStopDuration'}
                  type="number"
                  disabled={!hasFieldAccess('nextStop', 'edit')}
                />

                <FieldWithGeneration
                  label="Transport Options (comma-separated)"
                  value={poi.nextStop?.transportOptions?.join(', ') || ''}
                  onChange={handleTransportOptionsChange}
                  onGenerate={() => generateFieldWithPrompt('transportOptions')}
                  isGenerating={isGenerating === 'transportOptions'}
                  placeholder="walking, bus, metro"
                  fieldName="transportOptions"
                  disabled={!hasFieldAccess('nextStop', 'edit')}
                />
              </div>
            </div>
        </div>
        )}

        {/* SECTION: Archetype Classification */}
        <div id="poi-archetype" className="pt-4">
          <StopArchetypeClassifier
            poi={poi}
            onUpdate={(updatedPoi) => onPoiChange(updatedPoi)}
          />
        </div>

        {/* SECTION: Actions - IMPROVED ERROR HANDLING */}
        <div id="poi-actions" className="pt-6">
            {(() => {
              const missingFields = getMissingFields();
              const hasValidationErrors = missingFields.length > 0;
              const actionName = poi.id ? 'update_poi' : 'create_poi';
              const canPerform = canPerformAction(actionName);

              // Show validation errors prominently if there are any
              if (hasValidationErrors && canPerform) {
                return (
                  <div className="space-y-4">
                    <ErrorSummary
                      missingFields={missingFields}
                      onFieldClick={scrollToField}
                    />
                    <button
                      onClick={handleSubmit}
                      className="w-full px-4 py-2 rounded flex items-center gap-2 justify-center bg-gray-400 text-gray-700 cursor-not-allowed"
                      disabled
                    >
                      <PlusCircle className="w-4 h-4" />
                      {poi.id ? 'Update Point of Interest' : 'Add Point of Interest'}
                      <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded ml-2">
                        {missingFields.length} required field{missingFields.length > 1 ? 's' : ''} missing
                      </span>
                    </button>
                    <p className="text-xs text-gray-600 text-center">
                      Complete the required fields above to enable saving.
                    </p>
                  </div>
                );
              }

              if (canPerform) {
              return (
                <div className="space-y-2">
                  <SuccessMessage />
                  <button
                    onClick={handleSubmit}
                    className="w-full px-4 py-2 rounded flex items-center gap-2 justify-center bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                  >
                    <PlusCircle className="w-4 h-4" />
                    {poi.id ? 'Update Point of Interest' : 'Add Point of Interest'}
                    {!poi.audioUrl && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded ml-2">
                        Audio not included (optional)
                      </span>
                    )}
                  </button>
                </div>
              );
              }

              // User doesn't have permission
              return (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">You don't have permission to {poi.id ? 'update' : 'create'} points of interest.</span>
                  </div>
                </div>
              );
            })()}
        </div>

      </fieldset>
    </div>
  );
};

export default EnhancedPOIForm;
